/* Generated by scripts/generate-dcs-tools.ts
 * *DO NOT EDIT MANUALLY*
 */
import { z } from 'zod/v3';

export const BashCodeExecution = z.object({ input: z.string() });

export const BashCodeExecutionResponse = z.object({
  tool_use_id: z.string(),
  content: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.intersection(
        z.object({
          stdout: z.string(),
          stderr: z.string(),
          return_code: z.number().int(),
          content: z
            .union([z.array(z.object({ file_id: z.string() })), z.null()])
            .optional(),
        }),
        z.object({ type: z.literal('bash_code_execution_result') })
      ),
      z.intersection(
        z.object({
          error_code: z.any().superRefine((x, ctx) => {
            const schemas = [
              z.literal('unavailable'),
              z.literal('execution_time_exceeded'),
              z.literal('container_expired'),
              z.literal('invalid_input'),
              z.literal('too_many_requests'),
              z.literal('file_not_found'),
              z.literal('string_not_found'),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
        }),
        z.object({ type: z.literal('bash_code_execution_tool_result_error') })
      ),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
});

export const BulkSetEntityPropertyOptions = z.object({
  entities: z.array(
    z.object({
      entity_type: z.enum([
        'document',
        'project',
        'chat',
        'thread',
        'channel',
        'call',
        'user',
        'company',
      ]),
      entity_id: z.string(),
    })
  ),
  property_definition_id: z.string().uuid(),
  add_option_ids: z.union([z.array(z.string().uuid()), z.null()]).optional(),
  remove_option_ids: z.union([z.array(z.string().uuid()), z.null()]).optional(),
});

export const BulkSetEntityPropertyOptionsResponse = z.object({
  results: z.array(
    z.object({
      entityType: z.string(),
      entityId: z.string(),
      status: z.string(),
      error: z.union([z.string(), z.null()]).optional(),
    })
  ),
  summary: z.string(),
});

export const CalculateSpreadsheet = z.object({
  documentId: z.string(),
  sheetId: z.union([z.string(), z.null()]).optional(),
  formulas: z.array(
    z
      .object({
        label: z.union([z.string(), z.null()]).optional(),
        formula: z.string(),
      })
      .strict()
  ),
  overrides: z
    .union([
      z.array(
        z
          .object({
            sheetId: z.string(),
            cells: z.array(
              z.object({ address: z.string(), value: z.string() }).strict()
            ),
          })
          .strict()
      ),
      z.null(),
    ])
    .optional(),
});

export const SpreadsheetResponse = z.any().superRefine((x, ctx) => {
  const schemas = [
    z.object({
      revision: z.string(),
      sheets: z.array(
        z.object({
          id: z.string(),
          name: z.string(),
          rowCount: z.number().int().gte(0),
          columnCount: z.number().int().gte(0),
          usedRange: z.union([z.string(), z.null()]).optional(),
          populatedCells: z.number().int().gte(0),
          formulaCells: z.number().int().gte(0),
          errorCells: z.number().int().gte(0),
        })
      ),
      ranges: z.array(
        z.object({
          sheetId: z.string(),
          sheetName: z.string(),
          range: z.string(),
          cells: z.array(
            z.object({
              address: z.string(),
              source: z.string(),
              formula: z.union([z.string(), z.null()]).optional(),
              style: z
                .union([
                  z
                    .object({
                      numberFormat: z.union([z.string(), z.null()]).optional(),
                      fontName: z.union([z.string(), z.null()]).optional(),
                      borderTopStyle: z
                        .union([
                          z.any().superRefine((x, ctx) => {
                            const schemas = [
                              z.literal(''),
                              z.literal('thin'),
                              z.literal('medium'),
                              z.literal('thick'),
                              z.literal('double'),
                              z.literal('dotted'),
                              z.literal('dashed'),
                              z.literal('dashDot'),
                              z.literal('dashDotDot'),
                              z.literal('slantDashDot'),
                              z.literal('hair'),
                              z.literal('mediumDashed'),
                              z.literal('mediumDashDot'),
                              z.literal('mediumDashDotDot'),
                            ];
                            const errors = schemas.reduce<z.ZodError[]>(
                              (errors, schema) =>
                                ((result) =>
                                  result.error
                                    ? [...errors, result.error]
                                    : errors)(schema.safeParse(x)),
                              []
                            );
                            if (schemas.length - errors.length !== 1) {
                              ctx.addIssue({
                                path: ctx.path,
                                code: 'invalid_union',
                                unionErrors: errors,
                                message:
                                  'Invalid input: Should pass single schema',
                              });
                            }
                          }),
                          z.null(),
                        ])
                        .optional(),
                      borderTopColor: z
                        .union([z.string(), z.null()])
                        .optional(),
                      borderRightStyle: z
                        .union([
                          z.any().superRefine((x, ctx) => {
                            const schemas = [
                              z.literal(''),
                              z.literal('thin'),
                              z.literal('medium'),
                              z.literal('thick'),
                              z.literal('double'),
                              z.literal('dotted'),
                              z.literal('dashed'),
                              z.literal('dashDot'),
                              z.literal('dashDotDot'),
                              z.literal('slantDashDot'),
                              z.literal('hair'),
                              z.literal('mediumDashed'),
                              z.literal('mediumDashDot'),
                              z.literal('mediumDashDotDot'),
                            ];
                            const errors = schemas.reduce<z.ZodError[]>(
                              (errors, schema) =>
                                ((result) =>
                                  result.error
                                    ? [...errors, result.error]
                                    : errors)(schema.safeParse(x)),
                              []
                            );
                            if (schemas.length - errors.length !== 1) {
                              ctx.addIssue({
                                path: ctx.path,
                                code: 'invalid_union',
                                unionErrors: errors,
                                message:
                                  'Invalid input: Should pass single schema',
                              });
                            }
                          }),
                          z.null(),
                        ])
                        .optional(),
                      borderRightColor: z
                        .union([z.string(), z.null()])
                        .optional(),
                      borderBottomStyle: z
                        .union([
                          z.any().superRefine((x, ctx) => {
                            const schemas = [
                              z.literal(''),
                              z.literal('thin'),
                              z.literal('medium'),
                              z.literal('thick'),
                              z.literal('double'),
                              z.literal('dotted'),
                              z.literal('dashed'),
                              z.literal('dashDot'),
                              z.literal('dashDotDot'),
                              z.literal('slantDashDot'),
                              z.literal('hair'),
                              z.literal('mediumDashed'),
                              z.literal('mediumDashDot'),
                              z.literal('mediumDashDotDot'),
                            ];
                            const errors = schemas.reduce<z.ZodError[]>(
                              (errors, schema) =>
                                ((result) =>
                                  result.error
                                    ? [...errors, result.error]
                                    : errors)(schema.safeParse(x)),
                              []
                            );
                            if (schemas.length - errors.length !== 1) {
                              ctx.addIssue({
                                path: ctx.path,
                                code: 'invalid_union',
                                unionErrors: errors,
                                message:
                                  'Invalid input: Should pass single schema',
                              });
                            }
                          }),
                          z.null(),
                        ])
                        .optional(),
                      borderBottomColor: z
                        .union([z.string(), z.null()])
                        .optional(),
                      borderLeftStyle: z
                        .union([
                          z.any().superRefine((x, ctx) => {
                            const schemas = [
                              z.literal(''),
                              z.literal('thin'),
                              z.literal('medium'),
                              z.literal('thick'),
                              z.literal('double'),
                              z.literal('dotted'),
                              z.literal('dashed'),
                              z.literal('dashDot'),
                              z.literal('dashDotDot'),
                              z.literal('slantDashDot'),
                              z.literal('hair'),
                              z.literal('mediumDashed'),
                              z.literal('mediumDashDot'),
                              z.literal('mediumDashDotDot'),
                            ];
                            const errors = schemas.reduce<z.ZodError[]>(
                              (errors, schema) =>
                                ((result) =>
                                  result.error
                                    ? [...errors, result.error]
                                    : errors)(schema.safeParse(x)),
                              []
                            );
                            if (schemas.length - errors.length !== 1) {
                              ctx.addIssue({
                                path: ctx.path,
                                code: 'invalid_union',
                                unionErrors: errors,
                                message:
                                  'Invalid input: Should pass single schema',
                              });
                            }
                          }),
                          z.null(),
                        ])
                        .optional(),
                      borderLeftColor: z
                        .union([z.string(), z.null()])
                        .optional(),
                      bold: z.union([z.boolean(), z.null()]).optional(),
                      italic: z.union([z.boolean(), z.null()]).optional(),
                      underline: z.union([z.boolean(), z.null()]).optional(),
                      strikethrough: z
                        .union([z.boolean(), z.null()])
                        .optional(),
                      fontFamily: z
                        .union([
                          z.any().superRefine((x, ctx) => {
                            const schemas = [
                              z.literal('sans'),
                              z.literal('serif'),
                              z.literal('mono'),
                            ];
                            const errors = schemas.reduce<z.ZodError[]>(
                              (errors, schema) =>
                                ((result) =>
                                  result.error
                                    ? [...errors, result.error]
                                    : errors)(schema.safeParse(x)),
                              []
                            );
                            if (schemas.length - errors.length !== 1) {
                              ctx.addIssue({
                                path: ctx.path,
                                code: 'invalid_union',
                                unionErrors: errors,
                                message:
                                  'Invalid input: Should pass single schema',
                              });
                            }
                          }),
                          z.null(),
                        ])
                        .optional(),
                      fontSize: z
                        .union([z.number().int().gte(0).lte(255), z.null()])
                        .optional(),
                      textColor: z.union([z.string(), z.null()]).optional(),
                      fillColor: z.union([z.string(), z.null()]).optional(),
                      horizontalAlign: z
                        .union([
                          z.any().superRefine((x, ctx) => {
                            const schemas = [
                              z.literal('auto'),
                              z.literal('left'),
                              z.literal('center'),
                              z.literal('right'),
                            ];
                            const errors = schemas.reduce<z.ZodError[]>(
                              (errors, schema) =>
                                ((result) =>
                                  result.error
                                    ? [...errors, result.error]
                                    : errors)(schema.safeParse(x)),
                              []
                            );
                            if (schemas.length - errors.length !== 1) {
                              ctx.addIssue({
                                path: ctx.path,
                                code: 'invalid_union',
                                unionErrors: errors,
                                message:
                                  'Invalid input: Should pass single schema',
                              });
                            }
                          }),
                          z.null(),
                        ])
                        .optional(),
                      verticalAlign: z
                        .union([
                          z.any().superRefine((x, ctx) => {
                            const schemas = [
                              z.literal('top'),
                              z.literal('middle'),
                              z.literal('bottom'),
                            ];
                            const errors = schemas.reduce<z.ZodError[]>(
                              (errors, schema) =>
                                ((result) =>
                                  result.error
                                    ? [...errors, result.error]
                                    : errors)(schema.safeParse(x)),
                              []
                            );
                            if (schemas.length - errors.length !== 1) {
                              ctx.addIssue({
                                path: ctx.path,
                                code: 'invalid_union',
                                unionErrors: errors,
                                message:
                                  'Invalid input: Should pass single schema',
                              });
                            }
                          }),
                          z.null(),
                        ])
                        .optional(),
                      wrap: z.union([z.boolean(), z.null()]).optional(),
                      borderTop: z.union([z.boolean(), z.null()]).optional(),
                      borderRight: z.union([z.boolean(), z.null()]).optional(),
                      borderBottom: z.union([z.boolean(), z.null()]).optional(),
                      borderLeft: z.union([z.boolean(), z.null()]).optional(),
                      decimals: z
                        .union([z.number().int().gte(-128).lte(127), z.null()])
                        .optional(),
                      format: z
                        .union([
                          z.any().superRefine((x, ctx) => {
                            const schemas = [
                              z.literal('general'),
                              z.literal('number'),
                              z.literal('currency'),
                              z.literal('percent'),
                              z.literal('date'),
                              z.literal('time'),
                              z.literal('scientific'),
                              z.literal('text'),
                            ];
                            const errors = schemas.reduce<z.ZodError[]>(
                              (errors, schema) =>
                                ((result) =>
                                  result.error
                                    ? [...errors, result.error]
                                    : errors)(schema.safeParse(x)),
                              []
                            );
                            if (schemas.length - errors.length !== 1) {
                              ctx.addIssue({
                                path: ctx.path,
                                code: 'invalid_union',
                                unionErrors: errors,
                                message:
                                  'Invalid input: Should pass single schema',
                              });
                            }
                          }),
                          z.null(),
                        ])
                        .optional(),
                    })
                    .strict(),
                  z.null(),
                ])
                .optional(),
              type: z.any().superRefine((x, ctx) => {
                const schemas = [
                  z.literal('blank'),
                  z.literal('number'),
                  z.literal('text'),
                  z.literal('boolean'),
                  z.literal('error'),
                ];
                const errors = schemas.reduce<z.ZodError[]>(
                  (errors, schema) =>
                    ((result) =>
                      result.error ? [...errors, result.error] : errors)(
                      schema.safeParse(x)
                    ),
                  []
                );
                if (schemas.length - errors.length !== 1) {
                  ctx.addIssue({
                    path: ctx.path,
                    code: 'invalid_union',
                    unionErrors: errors,
                    message: 'Invalid input: Should pass single schema',
                  });
                }
              }),
              value: z.any(),
              display: z.string(),
              error: z.union([z.string(), z.null()]).optional(),
            })
          ),
          truncated: z.boolean(),
        })
      ),
      warnings: z.array(z.string()),
      action: z.literal('read'),
    }),
    z.object({
      revision: z.string(),
      results: z.array(
        z.object({
          label: z.union([z.string(), z.null()]).optional(),
          formula: z.string(),
          type: z.any().superRefine((x, ctx) => {
            const schemas = [
              z.literal('blank'),
              z.literal('number'),
              z.literal('text'),
              z.literal('boolean'),
              z.literal('error'),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
          value: z.any(),
          display: z.string(),
          error: z.union([z.string(), z.null()]).optional(),
        })
      ),
      warnings: z.array(z.string()),
      action: z.literal('calculate'),
    }),
    z.object({
      revision: z.string(),
      applied: z.boolean(),
      changes: z.array(
        z.object({
          type: z.string(),
          sheetId: z.string(),
          summary: z.string(),
          range: z.union([z.string(), z.null()]).optional(),
        })
      ),
      sheets: z.array(
        z.object({
          id: z.string(),
          name: z.string(),
          rowCount: z.number().int().gte(0),
          columnCount: z.number().int().gte(0),
          usedRange: z.union([z.string(), z.null()]).optional(),
          populatedCells: z.number().int().gte(0),
          formulaCells: z.number().int().gte(0),
          errorCells: z.number().int().gte(0),
        })
      ),
      warnings: z.array(z.string()),
      action: z.literal('edit'),
    }),
  ];
  const errors = schemas.reduce<z.ZodError[]>(
    (errors, schema) =>
      ((result) => (result.error ? [...errors, result.error] : errors))(
        schema.safeParse(x)
      ),
    []
  );
  if (schemas.length - errors.length !== 1) {
    ctx.addIssue({
      path: ctx.path,
      code: 'invalid_union',
      unionErrors: errors,
      message: 'Invalid input: Should pass single schema',
    });
  }
});

export const ConfigureBot = z.object({
  botId: z.string().uuid(),
  name: z.union([z.string(), z.null()]).optional(),
  handle: z.union([z.string(), z.null()]).optional(),
  description: z.union([z.string(), z.null()]).optional(),
  avatarUrl: z.union([z.string(), z.null()]).optional(),
  hasAgent: z.union([z.boolean(), z.null()]).optional(),
});

export const ConfigureBotResponse = z.object({
  bot: z.object({
    botId: z.string().uuid(),
    owner: z.any().superRefine((x, ctx) => {
      const schemas = [
        z.object({ user_id: z.string(), type: z.literal('user') }),
        z.object({ team_id: z.string().uuid(), type: z.literal('team') }),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
    name: z.string(),
    handle: z.string(),
    description: z.union([z.string(), z.null()]).optional(),
    avatarUrl: z.union([z.string(), z.null()]).optional(),
    hasAgent: z.boolean(),
  }),
  summary: z.string(),
});

export const ContentSearch = z.object({
  query: z.string(),
  matchType: z
    .intersection(
      z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('partial'), z.literal('exact')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.any().default('partial')
    )
    .optional(),
  entityTypes: z
    .array(
      z.enum([
        'agent_sessions',
        'documents',
        'chats',
        'emails',
        'channels',
        'projects',
        'call_records',
        'calendar_events',
      ])
    )
    .optional(),
  inbox: z.union([z.string(), z.null()]).optional(),
  tags: z
    .union([
      z.array(
        z.object({
          label: z.string(),
          scope: z
            .union([
              z.any().superRefine((x, ctx) => {
                const schemas = [z.literal('personal'), z.literal('team')];
                const errors = schemas.reduce<z.ZodError[]>(
                  (errors, schema) =>
                    ((result) =>
                      result.error ? [...errors, result.error] : errors)(
                      schema.safeParse(x)
                    ),
                  []
                );
                if (schemas.length - errors.length !== 1) {
                  ctx.addIssue({
                    path: ctx.path,
                    code: 'invalid_union',
                    unionErrors: errors,
                    message: 'Invalid input: Should pass single schema',
                  });
                }
              }),
              z.null(),
            ])
            .optional(),
        })
      ),
      z.null(),
    ])
    .optional(),
  tagsMatch: z
    .any()
    .superRefine((x, ctx) => {
      const schemas = [z.literal('any'), z.literal('all')];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    })
    .optional(),
});

export const SearchToolResponse = z.object({
  results: z.array(
    z
      .object({
        tags: z
          .array(
            z.object({
              label: z.string(),
              scope: z.any().superRefine((x, ctx) => {
                const schemas = [z.literal('personal'), z.literal('team')];
                const errors = schemas.reduce<z.ZodError[]>(
                  (errors, schema) =>
                    ((result) =>
                      result.error ? [...errors, result.error] : errors)(
                      schema.safeParse(x)
                    ),
                  []
                );
                if (schemas.length - errors.length !== 1) {
                  ctx.addIssue({
                    path: ctx.path,
                    code: 'invalid_union',
                    unionErrors: errors,
                    message: 'Invalid input: Should pass single schema',
                  });
                }
              }),
            })
          )
          .optional(),
      })
      .and(
        z.any().superRefine((x, ctx) => {
          const schemas = [
            z.intersection(
              z.object({
                metadata: z
                  .union([
                    z.object({
                      created_at: z.string().datetime({ offset: true }),
                      updated_at: z.string().datetime({ offset: true }),
                      viewed_at: z
                        .union([
                          z.string().datetime({ offset: true }),
                          z.null(),
                        ])
                        .optional(),
                      project_id: z.union([z.string(), z.null()]).optional(),
                      deleted_at: z
                        .union([
                          z.string().datetime({ offset: true }),
                          z.null(),
                        ])
                        .optional(),
                    }),
                    z.null(),
                  ])
                  .optional(),
                id: z.string().uuid(),
                name: z.string(),
                owner_id: z.string(),
                document_id: z.string().uuid(),
                document_name: z.string(),
                file_type: z.union([z.string(), z.null()]).optional(),
                sub_type: z
                  .union([
                    z.any().superRefine((x, ctx) => {
                      const schemas = [
                        z.literal('task'),
                        z.literal('snippet'),
                        z.literal('skill'),
                        z.literal('initiative_description'),
                      ];
                      const errors = schemas.reduce<z.ZodError[]>(
                        (errors, schema) =>
                          ((result) =>
                            result.error ? [...errors, result.error] : errors)(
                            schema.safeParse(x)
                          ),
                        []
                      );
                      if (schemas.length - errors.length !== 1) {
                        ctx.addIssue({
                          path: ctx.path,
                          code: 'invalid_union',
                          unionErrors: errors,
                          message: 'Invalid input: Should pass single schema',
                        });
                      }
                    }),
                    z.null(),
                  ])
                  .optional(),
                document_search_results: z.array(
                  z.object({
                    node_id: z.union([z.string(), z.null()]).optional(),
                    highlight: z.object({
                      name: z.union([z.string(), z.null()]).optional(),
                      content: z.array(z.string()).optional(),
                      user_id: z.union([z.string(), z.null()]).optional(),
                      sender: z.union([z.string(), z.null()]).optional(),
                      recipients: z.array(z.string()).optional(),
                      cc: z.array(z.string()).optional(),
                      bcc: z.array(z.string()).optional(),
                    }),
                    raw_content: z.union([z.string(), z.null()]).optional(),
                    score: z.union([z.number(), z.null()]).optional(),
                  })
                ),
              }),
              z.object({ type: z.literal('document') })
            ),
            z.intersection(
              z.object({
                metadata: z
                  .union([
                    z.object({
                      created_at: z.string().datetime({ offset: true }),
                      updated_at: z.string().datetime({ offset: true }),
                      viewed_at: z
                        .union([
                          z.string().datetime({ offset: true }),
                          z.null(),
                        ])
                        .optional(),
                      project_id: z.union([z.string(), z.null()]).optional(),
                      deleted_at: z
                        .union([
                          z.string().datetime({ offset: true }),
                          z.null(),
                        ])
                        .optional(),
                    }),
                    z.null(),
                  ])
                  .optional(),
                id: z.string().uuid(),
                name: z.string(),
                owner_id: z.string(),
                chat_id: z.string().uuid(),
                user_id: z.string(),
                chat_search_results: z.array(
                  z.object({
                    chat_message_id: z
                      .union([z.string().uuid(), z.null()])
                      .optional(),
                    role: z.union([z.string(), z.null()]).optional(),
                    highlight: z.object({
                      name: z.union([z.string(), z.null()]).optional(),
                      content: z.array(z.string()).optional(),
                      user_id: z.union([z.string(), z.null()]).optional(),
                      sender: z.union([z.string(), z.null()]).optional(),
                      recipients: z.array(z.string()).optional(),
                      cc: z.array(z.string()).optional(),
                      bcc: z.array(z.string()).optional(),
                    }),
                    score: z.union([z.number(), z.null()]).optional(),
                  })
                ),
              }),
              z.object({ type: z.literal('chat') })
            ),
            z.intersection(
              z.object({
                created_at: z.string().datetime({ offset: true }),
                updated_at: z.string().datetime({ offset: true }),
                viewed_at: z
                  .union([z.string().datetime({ offset: true }), z.null()])
                  .optional(),
                snippet: z.union([z.string(), z.null()]).optional(),
                is_read: z.boolean(),
                inbox_visible: z.boolean(),
                is_draft: z.boolean(),
                is_important: z.boolean(),
                id: z.string().uuid(),
                name: z.union([z.string(), z.null()]).optional(),
                owner_id: z.string(),
                subject: z.union([z.string(), z.null()]).optional(),
                thread_id: z.string().uuid(),
                user_id: z.string(),
                link_id: z.string().uuid(),
                email_message_search_results: z.array(
                  z.object({
                    message_id: z
                      .union([z.string().uuid(), z.null()])
                      .optional(),
                    sender: z.string(),
                    pretty_sender: z.string(),
                    recipients: z.array(z.string()),
                    cc: z.array(z.string()),
                    bcc: z.array(z.string()),
                    labels: z.array(z.string()),
                    sent_at: z
                      .union([z.string().datetime({ offset: true }), z.null()])
                      .optional(),
                    highlight: z.object({
                      name: z.union([z.string(), z.null()]).optional(),
                      content: z.array(z.string()).optional(),
                      user_id: z.union([z.string(), z.null()]).optional(),
                      sender: z.union([z.string(), z.null()]).optional(),
                      recipients: z.array(z.string()).optional(),
                      cc: z.array(z.string()).optional(),
                      bcc: z.array(z.string()).optional(),
                    }),
                    score: z.union([z.number(), z.null()]).optional(),
                  })
                ),
                participants: z.array(
                  z.object({
                    email: z.string(),
                    name: z.union([z.string(), z.null()]).optional(),
                  })
                ),
              }),
              z.object({ type: z.literal('email') })
            ),
            z.intersection(
              z.object({
                id: z.string().uuid(),
                owner_id: z.union([z.string(), z.null()]).optional(),
                channel_type: z.string(),
                channel_id: z.string().uuid(),
                message_id: z.string().uuid(),
                thread_id: z.union([z.string().uuid(), z.null()]).optional(),
                sender_id: z.string(),
                created_at: z.string().datetime({ offset: true }),
                updated_at: z.string().datetime({ offset: true }),
                deleted_at: z
                  .union([z.string().datetime({ offset: true }), z.null()])
                  .optional(),
                highlight: z.object({
                  name: z.union([z.string(), z.null()]).optional(),
                  content: z.array(z.string()).optional(),
                  user_id: z.union([z.string(), z.null()]).optional(),
                  sender: z.union([z.string(), z.null()]).optional(),
                  recipients: z.array(z.string()).optional(),
                  cc: z.array(z.string()).optional(),
                  bcc: z.array(z.string()).optional(),
                }),
                score: z.union([z.number(), z.null()]).optional(),
              }),
              z.object({ type: z.literal('channelMessage') })
            ),
            z.intersection(
              z.object({
                metadata: z
                  .union([
                    z.object({
                      created_at: z.string().datetime({ offset: true }),
                      updated_at: z.string().datetime({ offset: true }),
                      viewed_at: z
                        .union([
                          z.string().datetime({ offset: true }),
                          z.null(),
                        ])
                        .optional(),
                      interacted_at: z
                        .union([
                          z.string().datetime({ offset: true }),
                          z.null(),
                        ])
                        .optional(),
                    }),
                    z.null(),
                  ])
                  .optional(),
                id: z.string().uuid(),
                owner_id: z.union([z.string(), z.null()]).optional(),
                channel_type: z.string(),
                channel_id: z.string().uuid(),
                highlight: z.object({
                  name: z.union([z.string(), z.null()]).optional(),
                  content: z.array(z.string()).optional(),
                  user_id: z.union([z.string(), z.null()]).optional(),
                  sender: z.union([z.string(), z.null()]).optional(),
                  recipients: z.array(z.string()).optional(),
                  cc: z.array(z.string()).optional(),
                  bcc: z.array(z.string()).optional(),
                }),
                score: z.union([z.number(), z.null()]).optional(),
              }),
              z.object({ type: z.literal('channel') })
            ),
            z.intersection(
              z.object({
                metadata: z
                  .union([
                    z.object({
                      created_at: z.string().datetime({ offset: true }),
                      updated_at: z.string().datetime({ offset: true }),
                      viewed_at: z
                        .union([
                          z.string().datetime({ offset: true }),
                          z.null(),
                        ])
                        .optional(),
                      parent_project_id: z
                        .union([z.string(), z.null()])
                        .optional(),
                      deleted_at: z
                        .union([
                          z.string().datetime({ offset: true }),
                          z.null(),
                        ])
                        .optional(),
                    }),
                    z.null(),
                  ])
                  .optional(),
                id: z.string().uuid(),
                name: z.string(),
                owner_id: z.string(),
                updated_at: z.string().datetime({ offset: true }),
                created_at: z.string().datetime({ offset: true }),
                project_search_results: z.array(
                  z.object({
                    highlight: z.object({
                      name: z.union([z.string(), z.null()]).optional(),
                      content: z.array(z.string()).optional(),
                      user_id: z.union([z.string(), z.null()]).optional(),
                      sender: z.union([z.string(), z.null()]).optional(),
                      recipients: z.array(z.string()).optional(),
                      cc: z.array(z.string()).optional(),
                      bcc: z.array(z.string()).optional(),
                    }),
                    score: z.union([z.number(), z.null()]).optional(),
                  })
                ),
              }),
              z.object({ type: z.literal('project') })
            ),
            z.intersection(
              z.object({
                metadata: z
                  .union([
                    z.object({
                      created_by: z.string(),
                      started_at: z.string().datetime({ offset: true }),
                      ended_at: z.string().datetime({ offset: true }),
                      duration_ms: z.number().int(),
                      updated_at: z.string().datetime({ offset: true }),
                      channel_name: z.union([z.string(), z.null()]).optional(),
                      status: z.any().superRefine((x, ctx) => {
                        const schemas = [
                          z.literal('ATTENDED'),
                          z.literal('MISSED'),
                          z.literal('UNATTENDED'),
                        ];
                        const errors = schemas.reduce<z.ZodError[]>(
                          (errors, schema) =>
                            ((result) =>
                              result.error
                                ? [...errors, result.error]
                                : errors)(schema.safeParse(x)),
                          []
                        );
                        if (schemas.length - errors.length !== 1) {
                          ctx.addIssue({
                            path: ctx.path,
                            code: 'invalid_union',
                            unionErrors: errors,
                            message: 'Invalid input: Should pass single schema',
                          });
                        }
                      }),
                      attended: z.boolean(),
                    }),
                    z.null(),
                  ])
                  .optional(),
                id: z.string().uuid(),
                name: z.union([z.string(), z.null()]).optional(),
                owner_id: z.string(),
                call_id: z.string().uuid(),
                channel_id: z.string().uuid(),
                participant_ids: z.array(z.string()),
                call_search_results: z.array(
                  z.object({
                    transcript_id: z
                      .union([z.string().uuid(), z.null()])
                      .optional(),
                    speaker_id: z.union([z.string(), z.null()]).optional(),
                    sequence_num: z
                      .union([z.number().int(), z.null()])
                      .optional(),
                    started_at: z
                      .union([z.string().datetime({ offset: true }), z.null()])
                      .optional(),
                    ended_at: z
                      .union([z.string().datetime({ offset: true }), z.null()])
                      .optional(),
                    highlight: z.object({
                      name: z.union([z.string(), z.null()]).optional(),
                      content: z.array(z.string()).optional(),
                      user_id: z.union([z.string(), z.null()]).optional(),
                      sender: z.union([z.string(), z.null()]).optional(),
                      recipients: z.array(z.string()).optional(),
                      cc: z.array(z.string()).optional(),
                      bcc: z.array(z.string()).optional(),
                    }),
                    score: z.union([z.number(), z.null()]).optional(),
                  })
                ),
              }),
              z.object({ type: z.literal('call') })
            ),
            z.intersection(
              z.object({
                id: z.string().uuid(),
                teamId: z.string().uuid(),
                name: z.union([z.string(), z.null()]).optional(),
                nameHighlighted: z.union([z.string(), z.null()]).optional(),
                description: z.union([z.string(), z.null()]).optional(),
                hidden: z.boolean(),
                createdAt: z.string().datetime({ offset: true }),
                updatedAt: z.string().datetime({ offset: true }),
                domains: z.array(
                  z.object({
                    id: z.string().uuid(),
                    companyId: z.string().uuid(),
                    domain: z.string(),
                    createdAt: z.string().datetime({ offset: true }),
                  })
                ),
              }),
              z.object({ type: z.literal('company') })
            ),
            z.intersection(
              z.object({
                metadata: z
                  .union([
                    z.object({
                      createdAt: z.string().datetime({ offset: true }),
                      updatedAt: z.string().datetime({ offset: true }),
                      status: z.string(),
                      time: z.any().superRefine((x, ctx) => {
                        const schemas = [
                          z.object({
                            startsAt: z.string().datetime({ offset: true }),
                            endsAt: z.string().datetime({ offset: true }),
                            timeZone: z
                              .union([z.string(), z.null()])
                              .optional(),
                            kind: z.literal('timed'),
                          }),
                          z.object({
                            startDate: z.string().date(),
                            endDate: z.string().date(),
                            kind: z.literal('allDay'),
                          }),
                        ];
                        const errors = schemas.reduce<z.ZodError[]>(
                          (errors, schema) =>
                            ((result) =>
                              result.error
                                ? [...errors, result.error]
                                : errors)(schema.safeParse(x)),
                          []
                        );
                        if (schemas.length - errors.length !== 1) {
                          ctx.addIssue({
                            path: ctx.path,
                            code: 'invalid_union',
                            unionErrors: errors,
                            message: 'Invalid input: Should pass single schema',
                          });
                        }
                      }),
                      isRecurring: z.boolean(),
                      occurrence: z
                        .union([
                          z.object({
                            occurrenceKey: z.string(),
                            time: z.any().superRefine((x, ctx) => {
                              const schemas = [
                                z.object({
                                  startsAt: z
                                    .string()
                                    .datetime({ offset: true }),
                                  endsAt: z.string().datetime({ offset: true }),
                                  timeZone: z
                                    .union([z.string(), z.null()])
                                    .optional(),
                                  kind: z.literal('timed'),
                                }),
                                z.object({
                                  startDate: z.string().date(),
                                  endDate: z.string().date(),
                                  kind: z.literal('allDay'),
                                }),
                              ];
                              const errors = schemas.reduce<z.ZodError[]>(
                                (errors, schema) =>
                                  ((result) =>
                                    result.error
                                      ? [...errors, result.error]
                                      : errors)(schema.safeParse(x)),
                                []
                              );
                              if (schemas.length - errors.length !== 1) {
                                ctx.addIssue({
                                  path: ctx.path,
                                  code: 'invalid_union',
                                  unionErrors: errors,
                                  message:
                                    'Invalid input: Should pass single schema',
                                });
                              }
                            }),
                          }),
                          z.null(),
                        ])
                        .optional(),
                      conferenceUrl: z.union([z.string(), z.null()]).optional(),
                      isReadOnly: z.boolean(),
                      organizer: z
                        .union([
                          z.object({
                            name: z.union([z.string(), z.null()]).optional(),
                            email: z.union([z.string(), z.null()]).optional(),
                          }),
                          z.null(),
                        ])
                        .optional(),
                      description: z.union([z.string(), z.null()]).optional(),
                    }),
                    z.null(),
                  ])
                  .optional(),
                id: z.string().uuid(),
                name: z.string(),
                owner_id: z.string(),
                updated_at: z.string().datetime({ offset: true }),
                created_at: z.string().datetime({ offset: true }),
                calendar_event_search_results: z.array(
                  z.object({
                    highlight: z.object({
                      name: z.union([z.string(), z.null()]).optional(),
                      content: z.array(z.string()).optional(),
                      user_id: z.union([z.string(), z.null()]).optional(),
                      sender: z.union([z.string(), z.null()]).optional(),
                      recipients: z.array(z.string()).optional(),
                      cc: z.array(z.string()).optional(),
                      bcc: z.array(z.string()).optional(),
                    }),
                    score: z.union([z.number(), z.null()]).optional(),
                  })
                ),
              }),
              z.object({ type: z.literal('calendarEvent') })
            ),
            z.intersection(
              z.object({
                id: z.string().uuid(),
                name: z.string(),
                owner_id: z.string(),
                bot_id: z.string().uuid(),
                created_at: z.string().datetime({ offset: true }),
                updated_at: z.string().datetime({ offset: true }),
                agent_session_search_results: z.array(
                  z.object({
                    goto: z
                      .union([
                        z.object({
                          message_turn: z.number().int().gte(0),
                          author: z.any().superRefine((x, ctx) => {
                            const schemas = [
                              z.literal('user'),
                              z.literal('agent'),
                            ];
                            const errors = schemas.reduce<z.ZodError[]>(
                              (errors, schema) =>
                                ((result) =>
                                  result.error
                                    ? [...errors, result.error]
                                    : errors)(schema.safeParse(x)),
                              []
                            );
                            if (schemas.length - errors.length !== 1) {
                              ctx.addIssue({
                                path: ctx.path,
                                code: 'invalid_union',
                                unionErrors: errors,
                                message:
                                  'Invalid input: Should pass single schema',
                              });
                            }
                          }),
                        }),
                        z.null(),
                      ])
                      .optional(),
                    highlight: z.object({
                      name: z.union([z.string(), z.null()]).optional(),
                      content: z.array(z.string()).optional(),
                      user_id: z.union([z.string(), z.null()]).optional(),
                      sender: z.union([z.string(), z.null()]).optional(),
                      recipients: z.array(z.string()).optional(),
                      cc: z.array(z.string()).optional(),
                      bcc: z.array(z.string()).optional(),
                    }),
                    score: z.union([z.number(), z.null()]).optional(),
                  })
                ),
              }),
              z.object({ type: z.literal('agentSession') })
            ),
          ];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        })
      )
  ),
});

export const CreateBot = z.object({
  teamId: z.union([z.string().uuid(), z.null()]).optional(),
  name: z.string(),
  handle: z.string(),
  description: z.union([z.string(), z.null()]).optional(),
  avatarUrl: z.union([z.string(), z.null()]).optional(),
  channelId: z.union([z.string().uuid(), z.null()]).optional(),
  credentialLabel: z.union([z.string(), z.null()]).optional(),
  credentialExpiresAt: z
    .union([z.string().datetime({ offset: true }), z.null()])
    .optional(),
  hasAgent: z.union([z.boolean(), z.null()]).optional(),
});

export const CreateBotResponse = z.object({
  bot: z.object({
    botId: z.string().uuid(),
    owner: z.any().superRefine((x, ctx) => {
      const schemas = [
        z.object({ user_id: z.string(), type: z.literal('user') }),
        z.object({ team_id: z.string().uuid(), type: z.literal('team') }),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
    name: z.string(),
    handle: z.string(),
    description: z.union([z.string(), z.null()]).optional(),
    avatarUrl: z.union([z.string(), z.null()]).optional(),
    hasAgent: z.boolean(),
  }),
  channelSetup: z
    .union([
      z.object({
        channelId: z.string().uuid(),
        webhook: z.object({
          channelId: z.string().uuid(),
          channelName: z.union([z.string(), z.null()]).optional(),
          webhookUrl: z.string(),
        }),
        credentialHeader: z.string(),
        credentialScopeHeader: z.string(),
        credentialScope: z.string(),
        credentialLabel: z.union([z.string(), z.null()]).optional(),
        credentialExpiresAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
      }),
      z.null(),
    ])
    .optional(),
  summary: z.string(),
});

export const CreateCalendarEvent = z.object({
  title: z.string(),
  time: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.object({
        startsAt: z.string().datetime({ offset: true }),
        endsAt: z.string().datetime({ offset: true }),
        timeZone: z.union([z.string(), z.null()]).optional(),
        kind: z.literal('timed'),
      }),
      z.object({
        startDate: z.string().date(),
        endDate: z.string().date(),
        kind: z.literal('allDay'),
      }),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  description: z.union([z.string(), z.null()]).optional(),
  location: z.union([z.string(), z.null()]).optional(),
  attendees: z
    .array(z.object({ email: z.string(), isOptional: z.boolean().optional() }))
    .optional(),
  recurrenceLines: z.array(z.string()).optional(),
  calendarId: z.union([z.string().uuid(), z.null()]).optional(),
  reminders: z
    .union([
      z.object({
        useDefault: z.boolean(),
        overrides: z
          .array(
            z.object({ method: z.string(), minutes: z.number().int().gte(0) })
          )
          .optional(),
      }),
      z.null(),
    ])
    .optional(),
  addGoogleMeet: z.boolean().optional(),
  eventType: z
    .any()
    .superRefine((x, ctx) => {
      const schemas = [z.literal('default'), z.literal('out_of_office')];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    })
    .optional(),
  outOfOffice: z
    .union([
      z.object({
        autoDeclineMode: z
          .union([
            z.any().superRefine((x, ctx) => {
              const schemas = [
                z.literal('decline_none'),
                z.literal('decline_all'),
                z.literal('decline_new_only'),
              ];
              const errors = schemas.reduce<z.ZodError[]>(
                (errors, schema) =>
                  ((result) =>
                    result.error ? [...errors, result.error] : errors)(
                    schema.safeParse(x)
                  ),
                []
              );
              if (schemas.length - errors.length !== 1) {
                ctx.addIssue({
                  path: ctx.path,
                  code: 'invalid_union',
                  unionErrors: errors,
                  message: 'Invalid input: Should pass single schema',
                });
              }
            }),
            z.null(),
          ])
          .optional(),
        declineMessage: z.union([z.string(), z.null()]).optional(),
      }),
      z.null(),
    ])
    .optional(),
});

export const UserToolResponseForToolCalendarEvent = z
  .any()
  .superRefine((x, ctx) => {
    const schemas = [
      z.literal('PendingUserExecution'),
      z.literal('Rejected'),
      z
        .object({
          UserAction: z.object({
            eventId: z.string().uuid(),
            title: z.string(),
            start: z.string(),
            end: z.string(),
            isAllDay: z.boolean(),
            timeZone: z.union([z.string(), z.null()]).optional(),
            location: z.union([z.string(), z.null()]).optional(),
            description: z.union([z.string(), z.null()]).optional(),
            status: z.string(),
            isRecurring: z.boolean(),
            recurrenceLines: z.array(z.string()),
            attendees: z.array(
              z.object({
                email: z.string(),
                responseStatus: z.string(),
                isOrganizer: z.boolean(),
                isOptional: z.boolean(),
              })
            ),
            attendeeCount: z.number().int().gte(0),
            organizerEmail: z.union([z.string(), z.null()]).optional(),
            conferenceUrl: z.union([z.string(), z.null()]).optional(),
            isReadOnly: z.boolean(),
            calendarId: z.union([z.string().uuid(), z.null()]).optional(),
          }),
        })
        .strict(),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  });

export const CreateChannel = z.object({
  name: z.string(),
  channelType: z.any().superRefine((x, ctx) => {
    const schemas = [z.literal('private'), z.literal('team')];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  participants: z.array(z.string()).optional(),
});

export const CreateChannelResponse = z.object({
  channelId: z.string().uuid(),
  name: z.string(),
  channelType: z.any().superRefine((x, ctx) => {
    const schemas = [z.literal('private'), z.literal('team')];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  participants: z.array(z.string()),
  summary: z.string(),
});

export const CreateDocument = z.object({
  documentName: z.string(),
  fileContent: z.string(),
  fileExtension: z.string(),
  isTask: z.boolean().optional(),
  projectId: z.union([z.string().uuid(), z.null()]).optional(),
});

export const CreateDocumentResponse = z.object({
  documentId: z.string().uuid(),
});

export const CreateImportEntity = z.object({
  source: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('linear'),
      z.literal('notion'),
      z.literal('slack'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  foreignId: z.string(),
  status: z.any().superRefine((x, ctx) => {
    const schemas = [z.literal('staged'), z.literal('imported')];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  metadata: z.any(),
  entityId: z.union([z.string(), z.null()]).optional(),
});

export const CreateImportEntityResponse = z.object({
  outcome: z.string(),
  entity: z.object({
    id: z.string().uuid(),
    source: z.any().superRefine((x, ctx) => {
      const schemas = [
        z.literal('linear'),
        z.literal('notion'),
        z.literal('slack'),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
    foreignId: z.string(),
    status: z.any().superRefine((x, ctx) => {
      const schemas = [
        z.literal('staged'),
        z.literal('importing'),
        z.literal('imported'),
        z.literal('discarded'),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
    label: z.string(),
    entityId: z.union([z.string(), z.null()]).optional(),
    entityType: z.union([z.string(), z.null()]).optional(),
    importedByTeammate: z.boolean(),
  }),
  message: z.string(),
});

export const CreateProject = z.object({
  projectName: z.string(),
  parentProjectId: z.union([z.string().uuid(), z.null()]).optional(),
});

export const CreateProjectResponse = z.object({
  projectId: z.string().uuid(),
  projectName: z.string(),
});

export const CreateReminder = z.object({
  description: z.string(),
  remindAt: z.string().datetime({ offset: true }),
  entityType: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('document'),
          z.literal('ai_chat'),
          z.literal('project'),
          z.literal('email'),
          z.literal('channel'),
          z.literal('call'),
          z.literal('calendar_event'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
  entityId: z.union([z.string().uuid(), z.null()]).optional(),
});

export const ToolReminder = z.object({
  id: z.string().uuid(),
  description: z.string(),
  nextRunAt: z.string().datetime({ offset: true }),
  overdue: z.boolean(),
  recurrence: z.union([z.string(), z.null()]).optional(),
  entityType: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('document'),
          z.literal('ai_chat'),
          z.literal('project'),
          z.literal('email'),
          z.literal('channel'),
          z.literal('call'),
          z.literal('calendar_event'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
  entityId: z.union([z.string(), z.null()]).optional(),
  completed: z.boolean(),
  enabled: z.boolean(),
});

export const CreateTag = z.object({
  label: z.string(),
  color: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('red'),
      z.literal('tomato'),
      z.literal('orange'),
      z.literal('amber'),
      z.literal('yellow'),
      z.literal('green'),
      z.literal('teal'),
      z.literal('blue'),
      z.literal('indigo'),
      z.literal('purple'),
      z.literal('pink'),
      z.literal('gray'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  scope: z
    .intersection(
      z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('personal'), z.literal('team')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.any().default('personal')
    )
    .optional(),
});

export const CreateTagResponse = z.object({
  id: z.string().uuid(),
  label: z.string(),
  color: z.union([z.string(), z.null()]).optional(),
  propertyDefinitionId: z.string().uuid(),
  scope: z.any().superRefine((x, ctx) => {
    const schemas = [z.literal('personal'), z.literal('team')];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  summary: z.string(),
});

export const DeleteBot = z.object({ botId: z.string().uuid() });

export const DeleteBotResponse = z.object({
  botId: z.string().uuid(),
  deleted: z.boolean(),
  summary: z.string(),
});

export const DeleteCalendarEvent = z.object({
  eventId: z.string().uuid(),
  calendarId: z.union([z.string().uuid(), z.null()]).optional(),
  scope: z
    .any()
    .superRefine((x, ctx) => {
      const schemas = [
        z.literal('all'),
        z.literal('this_event'),
        z.literal('this_and_following'),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    })
    .optional(),
  recurrenceId: z.union([z.string(), z.null()]).optional(),
});

export const DeleteCalendarEventResponse = z.object({
  eventId: z.string().uuid(),
  summary: z.string(),
});

export const DeleteImportEntity = z.object({ id: z.string().uuid() });

export const DeleteImportEntityResponse = z.object({
  discarded: z.boolean(),
  message: z.string(),
});

export const DeleteReminder = z.object({ reminderId: z.string().uuid() });

export const DeleteReminderResponse = z.object({
  reminderId: z.string().uuid(),
  summary: z.string(),
});

export const DeleteTag = z.object({
  id: z.string().uuid(),
  property_definition_id: z.string().uuid(),
});

export const DeleteTagResponse = z.object({
  success: z.boolean(),
  message: z.string(),
});

export const DisplayResults = z.object({ view: z.any() });

export const DisplayResultsResponse = z.object({ message: z.string() });

export const EditDocument = z.object({
  document_id: z.string(),
  instructions: z.string(),
  fast: z.boolean().optional(),
});

export const EditDocumentResponse = z.object({
  summary: z.string(),
  clarification: z.union([z.string(), z.null()]).optional(),
});

export const EditSpreadsheet = z.object({
  documentId: z.string(),
  expectedRevision: z.string(),
  operations: z.array(
    z.any().superRefine((x, ctx) => {
      const schemas = [
        z
          .object({
            sheetId: z.string(),
            cells: z.array(
              z.object({ address: z.string(), value: z.string() }).strict()
            ),
            type: z.literal('set_cells'),
          })
          .strict(),
        z
          .object({
            sheetId: z.string(),
            range: z.string(),
            style: z
              .object({
                numberFormat: z.union([z.string(), z.null()]).optional(),
                fontName: z.union([z.string(), z.null()]).optional(),
                borderTopStyle: z
                  .union([
                    z.any().superRefine((x, ctx) => {
                      const schemas = [
                        z.literal(''),
                        z.literal('thin'),
                        z.literal('medium'),
                        z.literal('thick'),
                        z.literal('double'),
                        z.literal('dotted'),
                        z.literal('dashed'),
                        z.literal('dashDot'),
                        z.literal('dashDotDot'),
                        z.literal('slantDashDot'),
                        z.literal('hair'),
                        z.literal('mediumDashed'),
                        z.literal('mediumDashDot'),
                        z.literal('mediumDashDotDot'),
                      ];
                      const errors = schemas.reduce<z.ZodError[]>(
                        (errors, schema) =>
                          ((result) =>
                            result.error ? [...errors, result.error] : errors)(
                            schema.safeParse(x)
                          ),
                        []
                      );
                      if (schemas.length - errors.length !== 1) {
                        ctx.addIssue({
                          path: ctx.path,
                          code: 'invalid_union',
                          unionErrors: errors,
                          message: 'Invalid input: Should pass single schema',
                        });
                      }
                    }),
                    z.null(),
                  ])
                  .optional(),
                borderTopColor: z.union([z.string(), z.null()]).optional(),
                borderRightStyle: z
                  .union([
                    z.any().superRefine((x, ctx) => {
                      const schemas = [
                        z.literal(''),
                        z.literal('thin'),
                        z.literal('medium'),
                        z.literal('thick'),
                        z.literal('double'),
                        z.literal('dotted'),
                        z.literal('dashed'),
                        z.literal('dashDot'),
                        z.literal('dashDotDot'),
                        z.literal('slantDashDot'),
                        z.literal('hair'),
                        z.literal('mediumDashed'),
                        z.literal('mediumDashDot'),
                        z.literal('mediumDashDotDot'),
                      ];
                      const errors = schemas.reduce<z.ZodError[]>(
                        (errors, schema) =>
                          ((result) =>
                            result.error ? [...errors, result.error] : errors)(
                            schema.safeParse(x)
                          ),
                        []
                      );
                      if (schemas.length - errors.length !== 1) {
                        ctx.addIssue({
                          path: ctx.path,
                          code: 'invalid_union',
                          unionErrors: errors,
                          message: 'Invalid input: Should pass single schema',
                        });
                      }
                    }),
                    z.null(),
                  ])
                  .optional(),
                borderRightColor: z.union([z.string(), z.null()]).optional(),
                borderBottomStyle: z
                  .union([
                    z.any().superRefine((x, ctx) => {
                      const schemas = [
                        z.literal(''),
                        z.literal('thin'),
                        z.literal('medium'),
                        z.literal('thick'),
                        z.literal('double'),
                        z.literal('dotted'),
                        z.literal('dashed'),
                        z.literal('dashDot'),
                        z.literal('dashDotDot'),
                        z.literal('slantDashDot'),
                        z.literal('hair'),
                        z.literal('mediumDashed'),
                        z.literal('mediumDashDot'),
                        z.literal('mediumDashDotDot'),
                      ];
                      const errors = schemas.reduce<z.ZodError[]>(
                        (errors, schema) =>
                          ((result) =>
                            result.error ? [...errors, result.error] : errors)(
                            schema.safeParse(x)
                          ),
                        []
                      );
                      if (schemas.length - errors.length !== 1) {
                        ctx.addIssue({
                          path: ctx.path,
                          code: 'invalid_union',
                          unionErrors: errors,
                          message: 'Invalid input: Should pass single schema',
                        });
                      }
                    }),
                    z.null(),
                  ])
                  .optional(),
                borderBottomColor: z.union([z.string(), z.null()]).optional(),
                borderLeftStyle: z
                  .union([
                    z.any().superRefine((x, ctx) => {
                      const schemas = [
                        z.literal(''),
                        z.literal('thin'),
                        z.literal('medium'),
                        z.literal('thick'),
                        z.literal('double'),
                        z.literal('dotted'),
                        z.literal('dashed'),
                        z.literal('dashDot'),
                        z.literal('dashDotDot'),
                        z.literal('slantDashDot'),
                        z.literal('hair'),
                        z.literal('mediumDashed'),
                        z.literal('mediumDashDot'),
                        z.literal('mediumDashDotDot'),
                      ];
                      const errors = schemas.reduce<z.ZodError[]>(
                        (errors, schema) =>
                          ((result) =>
                            result.error ? [...errors, result.error] : errors)(
                            schema.safeParse(x)
                          ),
                        []
                      );
                      if (schemas.length - errors.length !== 1) {
                        ctx.addIssue({
                          path: ctx.path,
                          code: 'invalid_union',
                          unionErrors: errors,
                          message: 'Invalid input: Should pass single schema',
                        });
                      }
                    }),
                    z.null(),
                  ])
                  .optional(),
                borderLeftColor: z.union([z.string(), z.null()]).optional(),
                bold: z.union([z.boolean(), z.null()]).optional(),
                italic: z.union([z.boolean(), z.null()]).optional(),
                underline: z.union([z.boolean(), z.null()]).optional(),
                strikethrough: z.union([z.boolean(), z.null()]).optional(),
                fontFamily: z
                  .union([
                    z.any().superRefine((x, ctx) => {
                      const schemas = [
                        z.literal('sans'),
                        z.literal('serif'),
                        z.literal('mono'),
                      ];
                      const errors = schemas.reduce<z.ZodError[]>(
                        (errors, schema) =>
                          ((result) =>
                            result.error ? [...errors, result.error] : errors)(
                            schema.safeParse(x)
                          ),
                        []
                      );
                      if (schemas.length - errors.length !== 1) {
                        ctx.addIssue({
                          path: ctx.path,
                          code: 'invalid_union',
                          unionErrors: errors,
                          message: 'Invalid input: Should pass single schema',
                        });
                      }
                    }),
                    z.null(),
                  ])
                  .optional(),
                fontSize: z
                  .union([z.number().int().gte(0).lte(255), z.null()])
                  .optional(),
                textColor: z.union([z.string(), z.null()]).optional(),
                fillColor: z.union([z.string(), z.null()]).optional(),
                horizontalAlign: z
                  .union([
                    z.any().superRefine((x, ctx) => {
                      const schemas = [
                        z.literal('auto'),
                        z.literal('left'),
                        z.literal('center'),
                        z.literal('right'),
                      ];
                      const errors = schemas.reduce<z.ZodError[]>(
                        (errors, schema) =>
                          ((result) =>
                            result.error ? [...errors, result.error] : errors)(
                            schema.safeParse(x)
                          ),
                        []
                      );
                      if (schemas.length - errors.length !== 1) {
                        ctx.addIssue({
                          path: ctx.path,
                          code: 'invalid_union',
                          unionErrors: errors,
                          message: 'Invalid input: Should pass single schema',
                        });
                      }
                    }),
                    z.null(),
                  ])
                  .optional(),
                verticalAlign: z
                  .union([
                    z.any().superRefine((x, ctx) => {
                      const schemas = [
                        z.literal('top'),
                        z.literal('middle'),
                        z.literal('bottom'),
                      ];
                      const errors = schemas.reduce<z.ZodError[]>(
                        (errors, schema) =>
                          ((result) =>
                            result.error ? [...errors, result.error] : errors)(
                            schema.safeParse(x)
                          ),
                        []
                      );
                      if (schemas.length - errors.length !== 1) {
                        ctx.addIssue({
                          path: ctx.path,
                          code: 'invalid_union',
                          unionErrors: errors,
                          message: 'Invalid input: Should pass single schema',
                        });
                      }
                    }),
                    z.null(),
                  ])
                  .optional(),
                wrap: z.union([z.boolean(), z.null()]).optional(),
                borderTop: z.union([z.boolean(), z.null()]).optional(),
                borderRight: z.union([z.boolean(), z.null()]).optional(),
                borderBottom: z.union([z.boolean(), z.null()]).optional(),
                borderLeft: z.union([z.boolean(), z.null()]).optional(),
                decimals: z
                  .union([z.number().int().gte(-128).lte(127), z.null()])
                  .optional(),
                format: z
                  .union([
                    z.any().superRefine((x, ctx) => {
                      const schemas = [
                        z.literal('general'),
                        z.literal('number'),
                        z.literal('currency'),
                        z.literal('percent'),
                        z.literal('date'),
                        z.literal('time'),
                        z.literal('scientific'),
                        z.literal('text'),
                      ];
                      const errors = schemas.reduce<z.ZodError[]>(
                        (errors, schema) =>
                          ((result) =>
                            result.error ? [...errors, result.error] : errors)(
                            schema.safeParse(x)
                          ),
                        []
                      );
                      if (schemas.length - errors.length !== 1) {
                        ctx.addIssue({
                          path: ctx.path,
                          code: 'invalid_union',
                          unionErrors: errors,
                          message: 'Invalid input: Should pass single schema',
                        });
                      }
                    }),
                    z.null(),
                  ])
                  .optional(),
              })
              .strict(),
            type: z.literal('format_cells'),
          })
          .strict(),
        z
          .object({
            sheetId: z.string(),
            range: z.string(),
            clearFormatting: z.union([z.boolean(), z.null()]).optional(),
            type: z.literal('clear_cells'),
          })
          .strict(),
        z
          .object({
            sheetId: z.string(),
            sourceRange: z.string(),
            targetRange: z.string(),
            type: z.literal('fill_cells'),
          })
          .strict(),
        z.object({ name: z.string(), type: z.literal('add_sheet') }).strict(),
        z
          .object({
            sheetId: z.string(),
            name: z.string(),
            type: z.literal('rename_sheet'),
          })
          .strict(),
        z
          .object({
            sheetId: z.string(),
            name: z.union([z.string(), z.null()]).optional(),
            type: z.literal('duplicate_sheet'),
          })
          .strict(),
        z
          .object({ sheetId: z.string(), type: z.literal('delete_sheet') })
          .strict(),
        z
          .object({
            sheetId: z.string(),
            count: z.number().int().gte(0).lte(65535),
            type: z.literal('append_rows'),
          })
          .strict(),
        z
          .object({
            sheetId: z.string(),
            columns: z.array(
              z
                .object({
                  column: z.string(),
                  width: z.number().int().gte(0).lte(65535),
                })
                .strict()
            ),
            type: z.literal('resize_columns'),
          })
          .strict(),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    })
  ),
});

export const EditTag = z.object({
  id: z.string().uuid(),
  property_definition_id: z.string().uuid(),
  label: z.union([z.string(), z.null()]).optional(),
  color: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('red'),
          z.literal('tomato'),
          z.literal('orange'),
          z.literal('amber'),
          z.literal('yellow'),
          z.literal('green'),
          z.literal('teal'),
          z.literal('blue'),
          z.literal('indigo'),
          z.literal('purple'),
          z.literal('pink'),
          z.literal('gray'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
});

export const EditTagResponse = z.object({
  id: z.string().uuid(),
  label: z.string(),
  color: z.union([z.string(), z.null()]).optional(),
  propertyDefinitionId: z.string().uuid(),
  summary: z.string(),
});

export const GetBotWebhooks = z.object({ botId: z.string().uuid() });

export const GetBotWebhooksResponse = z.object({
  botId: z.string().uuid(),
  credentialHeader: z.string(),
  credentialScopeHeader: z.string(),
  credentialScope: z.string(),
  webhooks: z.array(
    z.object({
      channelId: z.string().uuid(),
      channelName: z.union([z.string(), z.null()]).optional(),
      webhookUrl: z.string(),
    })
  ),
  summary: z.string(),
});

export const GetCompany = z.object({ company_id: z.string().uuid() });

export const GetCompanyResponse = z.object({
  id: z.string().uuid(),
  name: z.union([z.string(), z.null()]).optional(),
  description: z.union([z.string(), z.null()]).optional(),
  domains: z.array(z.string()),
  hidden: z.boolean(),
  emailSync: z.boolean(),
  firstInteraction: z.string().datetime({ offset: true }),
  lastInteraction: z.string().datetime({ offset: true }),
  stage: z
    .union([
      z.object({ optionId: z.string().uuid(), label: z.string() }),
      z.null(),
    ])
    .optional(),
  ownerUserId: z.union([z.string(), z.null()]).optional(),
  revenue: z.union([z.number(), z.null()]).optional(),
  contacts: z.array(
    z.object({
      id: z.string().uuid(),
      email: z.string(),
      name: z.union([z.string(), z.null()]).optional(),
      lastInteraction: z.string().datetime({ offset: true }),
    })
  ),
  properties: z.array(
    z.object({
      propertyDefinitionId: z.string().uuid(),
      displayName: z.string(),
      dataType: z.string(),
      isMultiSelect: z.boolean(),
      isSystem: z.boolean(),
      currentValue: z.any().optional(),
      options: z.array(
        z.object({ id: z.string().uuid(), displayValue: z.string() })
      ),
    })
  ),
  summary: z.string(),
});

export const GetEntityProperties = z.object({
  entity_id: z.string(),
  entity_type: z.enum([
    'document',
    'project',
    'chat',
    'thread',
    'channel',
    'call',
    'user',
    'company',
  ]),
});

export const GetEntityPropertiesResponse = z.object({
  properties: z.array(
    z.object({
      propertyDefinitionId: z.string().uuid(),
      displayName: z.string(),
      dataType: z.string(),
      isMultiSelect: z.boolean(),
      isSystem: z.boolean(),
      currentValue: z.any().optional(),
      currentValueLabels: z.union([z.array(z.string()), z.null()]).optional(),
      scope: z
        .union([
          z.any().superRefine((x, ctx) => {
            const schemas = [z.literal('personal'), z.literal('team')];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
          z.null(),
        ])
        .optional(),
      options: z
        .array(
          z.object({
            id: z.string().uuid(),
            displayOrder: z.number().int(),
            displayValue: z.string(),
          })
        )
        .optional(),
    })
  ),
  summary: z.string(),
});

export const GetThread = z.object({
  threadId: z.string().uuid(),
  limit: z.union([z.number().int(), z.null()]).optional(),
});

export const GetThreadResponse = z.object({
  threadId: z.string().uuid(),
  isRead: z.boolean(),
  labels: z.array(z.string()),
  messages: z.array(
    z.object({
      id: z.string().uuid(),
      subject: z.union([z.string(), z.null()]).optional(),
      from: z
        .union([
          z.object({
            email: z.string(),
            name: z.union([z.string(), z.null()]).optional(),
          }),
          z.null(),
        ])
        .optional(),
      to: z.array(
        z.object({
          email: z.string(),
          name: z.union([z.string(), z.null()]).optional(),
        })
      ),
      cc: z.array(
        z.object({
          email: z.string(),
          name: z.union([z.string(), z.null()]).optional(),
        })
      ),
      bodyParsed: z.union([z.string(), z.null()]).optional(),
      date: z.union([z.string(), z.null()]).optional(),
      labels: z.array(z.string()),
    })
  ),
  summary: z.string(),
});

export const ImportNotionPage = z.object({ pageUrl: z.string() });

export const ImportNotionPageResponse = z.object({
  outcome: z.string(),
  entity: z.object({
    id: z.string().uuid(),
    source: z.any().superRefine((x, ctx) => {
      const schemas = [
        z.literal('linear'),
        z.literal('notion'),
        z.literal('slack'),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
    foreignId: z.string(),
    status: z.any().superRefine((x, ctx) => {
      const schemas = [
        z.literal('staged'),
        z.literal('importing'),
        z.literal('imported'),
        z.literal('discarded'),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
    label: z.string(),
    entityId: z.union([z.string(), z.null()]).optional(),
    entityType: z.union([z.string(), z.null()]).optional(),
    importedByTeammate: z.boolean(),
  }),
  message: z.string(),
});

export const IssueBotCredential = z.object({
  botId: z.string().uuid(),
  label: z.union([z.string(), z.null()]).optional(),
  expiresAt: z
    .union([z.string().datetime({ offset: true }), z.null()])
    .optional(),
});

export const IssueBotCredentialResponse = z.object({
  botId: z.string().uuid(),
  label: z.union([z.string(), z.null()]).optional(),
  expiresAt: z
    .union([z.string().datetime({ offset: true }), z.null()])
    .optional(),
  summary: z.string(),
});

export const ListBots = z.record(z.any());

export const ListBotsResponse = z.object({
  bots: z.array(
    z.object({
      botId: z.string().uuid(),
      owner: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.object({ user_id: z.string(), type: z.literal('user') }),
          z.object({ team_id: z.string().uuid(), type: z.literal('team') }),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      name: z.string(),
      handle: z.string(),
      description: z.union([z.string(), z.null()]).optional(),
      avatarUrl: z.union([z.string(), z.null()]).optional(),
      hasAgent: z.boolean(),
    })
  ),
  summary: z.string(),
});

export const ListCalendarEvents = z.object({
  start: z.string().datetime({ offset: true }),
  end: z.string().datetime({ offset: true }),
});

export const ListCalendarEventsResponse = z.object({
  events: z.array(
    z.object({
      eventId: z.string().uuid(),
      title: z.string(),
      start: z.string(),
      end: z.string(),
      isAllDay: z.boolean(),
      timeZone: z.union([z.string(), z.null()]).optional(),
      location: z.union([z.string(), z.null()]).optional(),
      description: z.union([z.string(), z.null()]).optional(),
      status: z.string(),
      eventType: z.union([z.string(), z.null()]).optional(),
      isRecurring: z.boolean(),
      recurrenceId: z.union([z.string(), z.null()]).optional(),
      attendees: z.array(
        z.object({
          email: z.string(),
          responseStatus: z.string(),
          isOrganizer: z.boolean(),
          isOptional: z.boolean(),
        })
      ),
      attendeeCount: z.number().int().gte(0),
      myResponse: z.union([z.string(), z.null()]).optional(),
      organizerEmail: z.union([z.string(), z.null()]).optional(),
      conferenceUrl: z.union([z.string(), z.null()]).optional(),
      isReadOnly: z.boolean(),
      calendarId: z.union([z.string().uuid(), z.null()]).optional(),
      copies: z
        .array(
          z.object({
            calendarId: z.string().uuid(),
            title: z.string(),
            isReadOnly: z.boolean(),
          })
        )
        .optional(),
    })
  ),
  truncated: z.boolean(),
  syncStatus: z.string(),
  summary: z.string(),
});

export const ListCalendars = z.record(z.any());

export const ListCalendarsToolResponse = z.object({
  calendars: z.array(
    z.object({
      calendarId: z.string().uuid(),
      name: z.string(),
      emailAddress: z.string(),
      isPrimary: z.boolean(),
      isWritable: z.boolean(),
    })
  ),
  summary: z.string(),
});

export const ListCompanies = z.object({
  search: z.union([z.string(), z.null()]).optional(),
  stage: z.union([z.string(), z.null()]).optional(),
  owner_user_id: z.union([z.string(), z.null()]).optional(),
  include_hidden: z.union([z.boolean(), z.null()]).optional(),
  limit: z.union([z.number().int().gte(0).lte(65535), z.null()]).optional(),
});

export const ListCompaniesResponse = z.object({
  companies: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.union([z.string(), z.null()]).optional(),
      domains: z.array(z.string()),
      hidden: z.boolean(),
      lastInteraction: z.string().datetime({ offset: true }),
      stage: z
        .union([
          z.object({ optionId: z.string().uuid(), label: z.string() }),
          z.null(),
        ])
        .optional(),
      ownerUserId: z.union([z.string(), z.null()]).optional(),
      revenue: z.union([z.number(), z.null()]).optional(),
    })
  ),
  summary: z.string(),
});

export const ListEntities = z.object({
  includeTypes: z
    .union([
      z.array(
        z.any().superRefine((x, ctx) => {
          const schemas = [
            z.literal('calendar_event'),
            z.literal('document'),
            z.literal('ai_chat'),
            z.literal('project'),
            z.literal('email'),
            z.literal('channel'),
            z.literal('channel_thread'),
            z.literal('call'),
            z.literal('foreign_entity'),
          ];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        })
      ),
      z.null(),
    ])
    .optional(),
  sortBy: z
    .any()
    .superRefine((x, ctx) => {
      const schemas = [
        z.literal('recently_viewed'),
        z.literal('recently_updated'),
        z.literal('recently_created'),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    })
    .optional(),
  df: z.any().optional(),
  pf: z.any().optional(),
  cf: z.any().optional(),
  emailPreset: z.union([z.literal('signal'), z.null()]).optional(),
  ef: z.any().optional(),
  chanf: z.any().optional(),
  cthf: z.any().optional(),
  callf: z.any().optional(),
  fef: z.any().optional(),
  propf: z.any().optional(),
  emailView: z.union([z.string(), z.null()]).optional(),
  inbox: z.union([z.string(), z.null()]).optional(),
  tags: z
    .union([
      z.array(
        z.object({
          label: z.string(),
          scope: z
            .union([
              z.any().superRefine((x, ctx) => {
                const schemas = [z.literal('personal'), z.literal('team')];
                const errors = schemas.reduce<z.ZodError[]>(
                  (errors, schema) =>
                    ((result) =>
                      result.error ? [...errors, result.error] : errors)(
                      schema.safeParse(x)
                    ),
                  []
                );
                if (schemas.length - errors.length !== 1) {
                  ctx.addIssue({
                    path: ctx.path,
                    code: 'invalid_union',
                    unionErrors: errors,
                    message: 'Invalid input: Should pass single schema',
                  });
                }
              }),
              z.null(),
            ])
            .optional(),
        })
      ),
      z.null(),
    ])
    .optional(),
  tagsMatch: z
    .any()
    .superRefine((x, ctx) => {
      const schemas = [z.literal('any'), z.literal('all')];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    })
    .optional(),
  limit: z.union([z.number().int().gte(0).lte(65535), z.null()]).optional(),
});

export const ListEntitiesResponse = z.object({
  items: z.array(
    z.any().superRefine((x, ctx) => {
      const schemas = [
        z.object({
          id: z.string().uuid(),
          title: z.string(),
          status: z.string(),
          location: z.union([z.string(), z.null()]).optional(),
          conferenceUrl: z.union([z.string(), z.null()]).optional(),
          conferenceProvider: z.union([z.string(), z.null()]).optional(),
          time: z.any(),
          tags: z
            .array(
              z.object({
                label: z.string(),
                scope: z.any().superRefine((x, ctx) => {
                  const schemas = [z.literal('personal'), z.literal('team')];
                  const errors = schemas.reduce<z.ZodError[]>(
                    (errors, schema) =>
                      ((result) =>
                        result.error ? [...errors, result.error] : errors)(
                        schema.safeParse(x)
                      ),
                    []
                  );
                  if (schemas.length - errors.length !== 1) {
                    ctx.addIssue({
                      path: ctx.path,
                      code: 'invalid_union',
                      unionErrors: errors,
                      message: 'Invalid input: Should pass single schema',
                    });
                  }
                }),
              })
            )
            .optional(),
          type: z.literal('calendarEvent'),
        }),
        z.object({
          id: z.string().uuid(),
          name: z.string(),
          fileType: z.union([z.string(), z.null()]).optional(),
          subType: z.union([z.string(), z.null()]).optional(),
          tags: z
            .array(
              z.object({
                label: z.string(),
                scope: z.any().superRefine((x, ctx) => {
                  const schemas = [z.literal('personal'), z.literal('team')];
                  const errors = schemas.reduce<z.ZodError[]>(
                    (errors, schema) =>
                      ((result) =>
                        result.error ? [...errors, result.error] : errors)(
                        schema.safeParse(x)
                      ),
                    []
                  );
                  if (schemas.length - errors.length !== 1) {
                    ctx.addIssue({
                      path: ctx.path,
                      code: 'invalid_union',
                      unionErrors: errors,
                      message: 'Invalid input: Should pass single schema',
                    });
                  }
                }),
              })
            )
            .optional(),
          type: z.literal('document'),
        }),
        z.object({
          id: z.string().uuid(),
          name: z.string(),
          tags: z
            .array(
              z.object({
                label: z.string(),
                scope: z.any().superRefine((x, ctx) => {
                  const schemas = [z.literal('personal'), z.literal('team')];
                  const errors = schemas.reduce<z.ZodError[]>(
                    (errors, schema) =>
                      ((result) =>
                        result.error ? [...errors, result.error] : errors)(
                        schema.safeParse(x)
                      ),
                    []
                  );
                  if (schemas.length - errors.length !== 1) {
                    ctx.addIssue({
                      path: ctx.path,
                      code: 'invalid_union',
                      unionErrors: errors,
                      message: 'Invalid input: Should pass single schema',
                    });
                  }
                }),
              })
            )
            .optional(),
          type: z.literal('aiChat'),
        }),
        z.object({
          id: z.string().uuid(),
          name: z.string(),
          tags: z
            .array(
              z.object({
                label: z.string(),
                scope: z.any().superRefine((x, ctx) => {
                  const schemas = [z.literal('personal'), z.literal('team')];
                  const errors = schemas.reduce<z.ZodError[]>(
                    (errors, schema) =>
                      ((result) =>
                        result.error ? [...errors, result.error] : errors)(
                        schema.safeParse(x)
                      ),
                    []
                  );
                  if (schemas.length - errors.length !== 1) {
                    ctx.addIssue({
                      path: ctx.path,
                      code: 'invalid_union',
                      unionErrors: errors,
                      message: 'Invalid input: Should pass single schema',
                    });
                  }
                }),
              })
            )
            .optional(),
          type: z.literal('project'),
        }),
        z.object({
          id: z.string().uuid(),
          subject: z.union([z.string(), z.null()]).optional(),
          snippet: z.union([z.string(), z.null()]).optional(),
          senderName: z.union([z.string(), z.null()]).optional(),
          senderEmail: z.union([z.string(), z.null()]).optional(),
          inboxVisible: z.boolean(),
          isRead: z.boolean(),
          isDraft: z.boolean(),
          tags: z
            .array(
              z.object({
                label: z.string(),
                scope: z.any().superRefine((x, ctx) => {
                  const schemas = [z.literal('personal'), z.literal('team')];
                  const errors = schemas.reduce<z.ZodError[]>(
                    (errors, schema) =>
                      ((result) =>
                        result.error ? [...errors, result.error] : errors)(
                        schema.safeParse(x)
                      ),
                    []
                  );
                  if (schemas.length - errors.length !== 1) {
                    ctx.addIssue({
                      path: ctx.path,
                      code: 'invalid_union',
                      unionErrors: errors,
                      message: 'Invalid input: Should pass single schema',
                    });
                  }
                }),
              })
            )
            .optional(),
          type: z.literal('email'),
        }),
        z.object({
          id: z.string().uuid(),
          name: z.union([z.string(), z.null()]).optional(),
          type: z.literal('channel'),
        }),
        z.object({
          id: z.string().uuid(),
          channelId: z.string().uuid(),
          type: z.literal('channelThread'),
        }),
        z.object({
          id: z.string().uuid(),
          createdBy: z.string(),
          tags: z
            .array(
              z.object({
                label: z.string(),
                scope: z.any().superRefine((x, ctx) => {
                  const schemas = [z.literal('personal'), z.literal('team')];
                  const errors = schemas.reduce<z.ZodError[]>(
                    (errors, schema) =>
                      ((result) =>
                        result.error ? [...errors, result.error] : errors)(
                        schema.safeParse(x)
                      ),
                    []
                  );
                  if (schemas.length - errors.length !== 1) {
                    ctx.addIssue({
                      path: ctx.path,
                      code: 'invalid_union',
                      unionErrors: errors,
                      message: 'Invalid input: Should pass single schema',
                    });
                  }
                }),
              })
            )
            .optional(),
          type: z.literal('call'),
        }),
        z.object({
          id: z.string().uuid(),
          foreignEntityId: z.string(),
          foreignEntitySource: z.string(),
          metadata: z.any(),
          type: z.literal('foreignEntity'),
        }),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    })
  ),
  summary: z.string(),
});

export const ListImportEntities = z.object({
  source: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('linear'),
          z.literal('notion'),
          z.literal('slack'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
  status: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('staged'),
          z.literal('importing'),
          z.literal('imported'),
          z.literal('discarded'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
});

export const ListImportEntitiesResponse = z.object({
  entities: z.array(
    z.object({
      id: z.string().uuid(),
      source: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('linear'),
          z.literal('notion'),
          z.literal('slack'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      foreignId: z.string(),
      status: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('staged'),
          z.literal('importing'),
          z.literal('imported'),
          z.literal('discarded'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      label: z.string(),
      entityId: z.union([z.string(), z.null()]).optional(),
      entityType: z.union([z.string(), z.null()]).optional(),
      importedByTeammate: z.boolean(),
    })
  ),
});

export const ListInboxes = z.record(z.any());

export const ListInboxesResponse = z.object({
  inboxes: z.array(
    z.object({
      emailAddress: z.string(),
      isPrimary: z.boolean(),
      isDelegated: z.boolean(),
    })
  ),
  summary: z.string(),
});

export const ListLabels = z.object({
  thread_id: z.union([z.string().uuid(), z.null()]).optional(),
  inbox: z.union([z.string(), z.null()]).optional(),
});

export const ListLabelsResponse = z.object({
  labels: z.array(
    z.object({ id: z.string().uuid(), name: z.string(), type: z.string() })
  ),
  summary: z.string(),
});

export const ListNotifications = z.object({
  limit: z.union([z.number().int().gte(0), z.null()]).optional(),
  states: z
    .union([
      z.array(
        z.any().superRefine((x, ctx) => {
          const schemas = [
            z.literal('unseen'),
            z.literal('seen'),
            z.literal('done'),
          ];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        })
      ),
      z.null(),
    ])
    .optional(),
  includeTypes: z
    .union([
      z.array(
        z.enum([
          'email',
          'message',
          'channel',
          'document',
          'project',
          'chat',
          'call',
          'task',
          'github',
          'reminder',
          'calendar',
          'agent',
        ])
      ),
      z.null(),
    ])
    .optional(),
  entities: z
    .union([
      z.array(
        z.object({
          entityType: z.enum([
            'user',
            'chat',
            'channel',
            'channel_message',
            'document',
            'project',
            'email_thread',
            'calendar_event',
            'team',
            'call',
            'foreign_entity',
            'static_file',
            'crm_company',
            'crm_contact',
            'reminder',
            'skill',
            'scheduled_action',
          ]),
          id: z.string(),
        })
      ),
      z.null(),
    ])
    .optional(),
});

export const ListNotificationsResponse = z.object({
  notifications: z.array(
    z.object({
      id: z.string().uuid(),
      eventType: z.string(),
      entityType: z.string(),
      entityId: z.string(),
      state: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('unseen'),
          z.literal('seen'),
          z.literal('done'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      createdAt: z.string(),
      metadata: z.any(),
      senderId: z.union([z.string(), z.null()]).optional(),
    })
  ),
  hasMore: z.boolean(),
});

export const ListReminders = z.object({
  reminderIds: z.union([z.array(z.string().uuid()), z.null()]).optional(),
  entityType: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('document'),
          z.literal('ai_chat'),
          z.literal('project'),
          z.literal('email'),
          z.literal('channel'),
          z.literal('call'),
          z.literal('calendar_event'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
  entityId: z.union([z.string().uuid(), z.null()]).optional(),
  completed: z.union([z.boolean(), z.null()]).optional(),
  overdue: z.union([z.boolean(), z.null()]).optional(),
  limit: z.union([z.number().int().gte(0), z.null()]).optional(),
});

export const ListRemindersResponse = z.object({
  reminders: z.array(
    z.object({
      id: z.string().uuid(),
      description: z.string(),
      nextRunAt: z.string().datetime({ offset: true }),
      overdue: z.boolean(),
      recurrence: z.union([z.string(), z.null()]).optional(),
      entityType: z
        .union([
          z.any().superRefine((x, ctx) => {
            const schemas = [
              z.literal('document'),
              z.literal('ai_chat'),
              z.literal('project'),
              z.literal('email'),
              z.literal('channel'),
              z.literal('call'),
              z.literal('calendar_event'),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
          z.null(),
        ])
        .optional(),
      entityId: z.union([z.string(), z.null()]).optional(),
      completed: z.boolean(),
      enabled: z.boolean(),
    })
  ),
  summary: z.string(),
});

export const ListSkills = z.record(z.any());

export const ListSkillsResponse = z.object({
  results: z.array(
    z.object({
      documentId: z.string().uuid(),
      name: z.string(),
      updatedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
    })
  ),
});

export const ListTags = z.record(z.any());

export const ListTagsResponse = z.object({
  tagSets: z.array(
    z.object({
      scope: z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('personal'), z.literal('team')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      propertyDefinitionId: z.string().uuid(),
      tags: z.array(
        z.object({
          id: z.string().uuid(),
          label: z.string(),
          color: z.union([z.string(), z.null()]).optional(),
        })
      ),
    })
  ),
  summary: z.string(),
});

export const ListTeamMembers = z.record(z.any());

export const ListTeamMembersResponse = z.object({
  members: z.array(z.object({ userId: z.string(), role: z.string() })),
  invited: z.array(z.object({ email: z.string(), role: z.string() })),
});

export const LoadTools = z.object({ names: z.array(z.string()) });

export const LoadToolsResponse = z.object({
  loaded: z.array(z.object({ name: z.string(), description: z.string() })),
  not_found: z.array(z.string()),
});

export const ManageBotChannelAccess = z.object({
  botId: z.string().uuid(),
  channelId: z.string().uuid(),
  action: z.any().superRefine((x, ctx) => {
    const schemas = [z.literal('grant'), z.literal('revoke')];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
});

export const ManageBotChannelAccessResponse = z.object({
  botId: z.string().uuid(),
  channelId: z.string().uuid(),
  action: z.any().superRefine((x, ctx) => {
    const schemas = [z.literal('grant'), z.literal('revoke')];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  summary: z.string(),
});

export const ManageChannelParticipants = z.object({
  channelId: z.string().uuid(),
  action: z.any().superRefine((x, ctx) => {
    const schemas = [z.literal('add'), z.literal('remove')];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  participants: z.array(z.string()),
});

export const ManageChannelParticipantsResponse = z.object({
  channelId: z.string().uuid(),
  action: z.any().superRefine((x, ctx) => {
    const schemas = [z.literal('add'), z.literal('remove')];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  participants: z.array(z.string()),
  summary: z.string(),
});

export const MarkNotificationsDone = z.object({
  notificationIds: z.array(z.string().uuid()),
  done: z.boolean(),
});

export const MarkNotificationsResponse = z.object({
  success: z.boolean(),
  count: z.number().int().gte(0),
});

export const MarkNotificationsSeen = z.object({
  notificationIds: z.array(z.string().uuid()),
});

export const MoveToProject = z.object({
  entityType: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('document'),
      z.literal('chat'),
      z.literal('email'),
      z.literal('project'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  entityId: z.string().uuid(),
  projectId: z.union([z.string().uuid(), z.null()]).optional(),
});

export const MoveToProjectResponse = z.object({
  success: z.boolean(),
  message: z.string(),
});

export const NameSearch = z.object({
  name: z.string(),
  matchType: z
    .intersection(
      z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('partial'), z.literal('exact')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.any().default('partial')
    )
    .optional(),
  entityTypes: z
    .array(
      z.enum([
        'agent_sessions',
        'documents',
        'chats',
        'emails',
        'channels',
        'projects',
        'call_records',
        'calendar_events',
      ])
    )
    .optional(),
  inbox: z.union([z.string(), z.null()]).optional(),
  tags: z
    .union([
      z.array(
        z.object({
          label: z.string(),
          scope: z
            .union([
              z.any().superRefine((x, ctx) => {
                const schemas = [z.literal('personal'), z.literal('team')];
                const errors = schemas.reduce<z.ZodError[]>(
                  (errors, schema) =>
                    ((result) =>
                      result.error ? [...errors, result.error] : errors)(
                      schema.safeParse(x)
                    ),
                  []
                );
                if (schemas.length - errors.length !== 1) {
                  ctx.addIssue({
                    path: ctx.path,
                    code: 'invalid_union',
                    unionErrors: errors,
                    message: 'Invalid input: Should pass single schema',
                  });
                }
              }),
              z.null(),
            ])
            .optional(),
        })
      ),
      z.null(),
    ])
    .optional(),
  tagsMatch: z
    .any()
    .superRefine((x, ctx) => {
      const schemas = [z.literal('any'), z.literal('all')];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    })
    .optional(),
});

export const ReadActivity = z.object({
  from: z.string().datetime({ offset: true }),
  to: z.string().datetime({ offset: true }),
});

export const ReadActivityResponse = z.object({
  activities: z.array(
    z.object({
      actorId: z.string(),
      entityType: z.string(),
      entityId: z.string(),
      action: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.object({ type: z.literal('created') }),
          z.object({ type: z.literal('edited') }),
          z.object({ type: z.literal('opened') }),
          z.object({ type: z.literal('deleted') }),
          z.object({ type: z.literal('messaged') }),
          z.object({ type: z.literal('sent') }),
          z.object({
            property: z.string(),
            propertyName: z.union([z.string(), z.null()]).optional(),
            propertyType: z.union([z.string(), z.null()]).optional(),
            from: z.any().optional(),
            fromLabels: z.union([z.array(z.string()), z.null()]).optional(),
            to: z.any().optional(),
            toLabels: z.union([z.array(z.string()), z.null()]).optional(),
            type: z.literal('propertyChanged'),
          }),
          z.object({
            participant: z.string(),
            type: z.literal('participantAdded'),
          }),
          z.object({
            participant: z.string(),
            type: z.literal('participantRemoved'),
          }),
          z.object({ callId: z.string(), type: z.literal('callStarted') }),
          z.object({
            tag: z.string(),
            payload: z.any().optional(),
            type: z.literal('unknown'),
          }),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      occurredAt: z.string().datetime({ offset: true }),
    })
  ),
  truncated: z.boolean(),
});

export const ReadCallRecord = z.object({ callId: z.string().uuid() });

export const ReadCallRecordResponse = z.object({
  callId: z.string().uuid(),
  summary: z.union([z.string(), z.null()]).optional(),
  transcript: z.array(
    z.object({
      speakerId: z.string(),
      diarizedSpeakerId: z.union([z.string(), z.null()]).optional(),
      content: z.string(),
      startedAt: z.string().datetime({ offset: true }),
      endedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
    })
  ),
});

export const ReadChannelMessageContext = z.object({
  channelId: z.string().uuid(),
  messageId: z.string().uuid(),
  channelBefore: z
    .union([z.number().int().gte(0).lte(65535), z.null()])
    .optional(),
  channelAfter: z
    .union([z.number().int().gte(0).lte(65535), z.null()])
    .optional(),
  threadBefore: z
    .union([z.number().int().gte(0).lte(65535), z.null()])
    .optional(),
  threadAfter: z
    .union([z.number().int().gte(0).lte(65535), z.null()])
    .optional(),
  maxCharsPerMessage: z.union([z.number().int().gte(0), z.null()]).optional(),
});

export const ReadChannelMessageContextResponse = z.object({
  anchor: z.object({
    messageId: z.string().uuid(),
    channelId: z.string().uuid(),
    kind: z.any().superRefine((x, ctx) => {
      const schemas = [z.literal('topLevelMessage'), z.literal('threadReply')];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
    threadId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
  }),
  channelContext: z.object({
    before: z.array(
      z.object({
        id: z.string().uuid(),
        channelId: z.string().uuid(),
        senderId: z.string(),
        content: z.string(),
        contentTruncated: z.boolean(),
        createdAt: z.string().datetime({ offset: true }),
        updatedAt: z.string().datetime({ offset: true }),
        editedAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
        deletedAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
        thread: z.object({
          threadId: z.string().uuid(),
          replyCount: z.number().int(),
          latestReplyAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .optional(),
          preview: z
            .union([
              z.array(
                z.object({
                  id: z.string().uuid(),
                  threadId: z.string().uuid(),
                  senderId: z.string(),
                  content: z.string(),
                  contentTruncated: z.boolean(),
                  createdAt: z.string().datetime({ offset: true }),
                  updatedAt: z.string().datetime({ offset: true }),
                  editedAt: z
                    .union([z.string().datetime({ offset: true }), z.null()])
                    .optional(),
                  reactions: z.array(
                    z.object({ emoji: z.string(), users: z.array(z.string()) })
                  ),
                  attachments: z.array(
                    z.object({
                      id: z.string().uuid(),
                      entityType: z.string(),
                      entityId: z.string(),
                      createdAt: z.string().datetime({ offset: true }),
                    })
                  ),
                })
              ),
              z.null(),
            ])
            .optional(),
          omittedReplyCount: z.number().int(),
        }),
        reactions: z.array(
          z.object({ emoji: z.string(), users: z.array(z.string()) })
        ),
        attachments: z.array(
          z.object({
            id: z.string().uuid(),
            entityType: z.string(),
            entityId: z.string(),
            createdAt: z.string().datetime({ offset: true }),
          })
        ),
      })
    ),
    anchorOrParent: z.object({
      id: z.string().uuid(),
      channelId: z.string().uuid(),
      senderId: z.string(),
      content: z.string(),
      contentTruncated: z.boolean(),
      createdAt: z.string().datetime({ offset: true }),
      updatedAt: z.string().datetime({ offset: true }),
      editedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
      deletedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
      thread: z.object({
        threadId: z.string().uuid(),
        replyCount: z.number().int(),
        latestReplyAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
        preview: z
          .union([
            z.array(
              z.object({
                id: z.string().uuid(),
                threadId: z.string().uuid(),
                senderId: z.string(),
                content: z.string(),
                contentTruncated: z.boolean(),
                createdAt: z.string().datetime({ offset: true }),
                updatedAt: z.string().datetime({ offset: true }),
                editedAt: z
                  .union([z.string().datetime({ offset: true }), z.null()])
                  .optional(),
                reactions: z.array(
                  z.object({ emoji: z.string(), users: z.array(z.string()) })
                ),
                attachments: z.array(
                  z.object({
                    id: z.string().uuid(),
                    entityType: z.string(),
                    entityId: z.string(),
                    createdAt: z.string().datetime({ offset: true }),
                  })
                ),
              })
            ),
            z.null(),
          ])
          .optional(),
        omittedReplyCount: z.number().int(),
      }),
      reactions: z.array(
        z.object({ emoji: z.string(), users: z.array(z.string()) })
      ),
      attachments: z.array(
        z.object({
          id: z.string().uuid(),
          entityType: z.string(),
          entityId: z.string(),
          createdAt: z.string().datetime({ offset: true }),
        })
      ),
    }),
    after: z.array(
      z.object({
        id: z.string().uuid(),
        channelId: z.string().uuid(),
        senderId: z.string(),
        content: z.string(),
        contentTruncated: z.boolean(),
        createdAt: z.string().datetime({ offset: true }),
        updatedAt: z.string().datetime({ offset: true }),
        editedAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
        deletedAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
        thread: z.object({
          threadId: z.string().uuid(),
          replyCount: z.number().int(),
          latestReplyAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .optional(),
          preview: z
            .union([
              z.array(
                z.object({
                  id: z.string().uuid(),
                  threadId: z.string().uuid(),
                  senderId: z.string(),
                  content: z.string(),
                  contentTruncated: z.boolean(),
                  createdAt: z.string().datetime({ offset: true }),
                  updatedAt: z.string().datetime({ offset: true }),
                  editedAt: z
                    .union([z.string().datetime({ offset: true }), z.null()])
                    .optional(),
                  reactions: z.array(
                    z.object({ emoji: z.string(), users: z.array(z.string()) })
                  ),
                  attachments: z.array(
                    z.object({
                      id: z.string().uuid(),
                      entityType: z.string(),
                      entityId: z.string(),
                      createdAt: z.string().datetime({ offset: true }),
                    })
                  ),
                })
              ),
              z.null(),
            ])
            .optional(),
          omittedReplyCount: z.number().int(),
        }),
        reactions: z.array(
          z.object({ emoji: z.string(), users: z.array(z.string()) })
        ),
        attachments: z.array(
          z.object({
            id: z.string().uuid(),
            entityType: z.string(),
            entityId: z.string(),
            createdAt: z.string().datetime({ offset: true }),
          })
        ),
      })
    ),
  }),
  threadContext: z
    .union([
      z.object({
        threadId: z.string().uuid(),
        parent: z.object({
          id: z.string().uuid(),
          channelId: z.string().uuid(),
          senderId: z.string(),
          content: z.string(),
          contentTruncated: z.boolean(),
          createdAt: z.string().datetime({ offset: true }),
          updatedAt: z.string().datetime({ offset: true }),
          editedAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .optional(),
          deletedAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .optional(),
          thread: z.object({
            threadId: z.string().uuid(),
            replyCount: z.number().int(),
            latestReplyAt: z
              .union([z.string().datetime({ offset: true }), z.null()])
              .optional(),
            preview: z
              .union([
                z.array(
                  z.object({
                    id: z.string().uuid(),
                    threadId: z.string().uuid(),
                    senderId: z.string(),
                    content: z.string(),
                    contentTruncated: z.boolean(),
                    createdAt: z.string().datetime({ offset: true }),
                    updatedAt: z.string().datetime({ offset: true }),
                    editedAt: z
                      .union([z.string().datetime({ offset: true }), z.null()])
                      .optional(),
                    reactions: z.array(
                      z.object({
                        emoji: z.string(),
                        users: z.array(z.string()),
                      })
                    ),
                    attachments: z.array(
                      z.object({
                        id: z.string().uuid(),
                        entityType: z.string(),
                        entityId: z.string(),
                        createdAt: z.string().datetime({ offset: true }),
                      })
                    ),
                  })
                ),
                z.null(),
              ])
              .optional(),
            omittedReplyCount: z.number().int(),
          }),
          reactions: z.array(
            z.object({ emoji: z.string(), users: z.array(z.string()) })
          ),
          attachments: z.array(
            z.object({
              id: z.string().uuid(),
              entityType: z.string(),
              entityId: z.string(),
              createdAt: z.string().datetime({ offset: true }),
            })
          ),
        }),
        repliesBefore: z.array(
          z.object({
            id: z.string().uuid(),
            threadId: z.string().uuid(),
            senderId: z.string(),
            content: z.string(),
            contentTruncated: z.boolean(),
            createdAt: z.string().datetime({ offset: true }),
            updatedAt: z.string().datetime({ offset: true }),
            editedAt: z
              .union([z.string().datetime({ offset: true }), z.null()])
              .optional(),
            reactions: z.array(
              z.object({ emoji: z.string(), users: z.array(z.string()) })
            ),
            attachments: z.array(
              z.object({
                id: z.string().uuid(),
                entityType: z.string(),
                entityId: z.string(),
                createdAt: z.string().datetime({ offset: true }),
              })
            ),
          })
        ),
        anchorReply: z
          .union([
            z.object({
              id: z.string().uuid(),
              threadId: z.string().uuid(),
              senderId: z.string(),
              content: z.string(),
              contentTruncated: z.boolean(),
              createdAt: z.string().datetime({ offset: true }),
              updatedAt: z.string().datetime({ offset: true }),
              editedAt: z
                .union([z.string().datetime({ offset: true }), z.null()])
                .optional(),
              reactions: z.array(
                z.object({ emoji: z.string(), users: z.array(z.string()) })
              ),
              attachments: z.array(
                z.object({
                  id: z.string().uuid(),
                  entityType: z.string(),
                  entityId: z.string(),
                  createdAt: z.string().datetime({ offset: true }),
                })
              ),
            }),
            z.null(),
          ])
          .optional(),
        repliesAfter: z.array(
          z.object({
            id: z.string().uuid(),
            threadId: z.string().uuid(),
            senderId: z.string(),
            content: z.string(),
            contentTruncated: z.boolean(),
            createdAt: z.string().datetime({ offset: true }),
            updatedAt: z.string().datetime({ offset: true }),
            editedAt: z
              .union([z.string().datetime({ offset: true }), z.null()])
              .optional(),
            reactions: z.array(
              z.object({ emoji: z.string(), users: z.array(z.string()) })
            ),
            attachments: z.array(
              z.object({
                id: z.string().uuid(),
                entityType: z.string(),
                entityId: z.string(),
                createdAt: z.string().datetime({ offset: true }),
              })
            ),
          })
        ),
        omittedBefore: z.number().int().gte(0),
        omittedAfter: z.number().int().gte(0),
      }),
      z.null(),
    ])
    .optional(),
  omissions: z.array(
    z.object({
      kind: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('olderMessages'),
          z.literal('newerMessages'),
          z.literal('threadReplies'),
          z.literal('truncatedContent'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      messageId: z.union([z.string().uuid(), z.null()]).optional(),
      threadId: z.union([z.string().uuid(), z.null()]).optional(),
      count: z.union([z.number().int(), z.null()]).optional(),
      cursor: z.union([z.string(), z.null()]).optional(),
    })
  ),
});

export const ReadChannelMessages = z.object({
  channelId: z.string().uuid(),
  windowType: z.enum([
    'latest',
    'timeRange',
    'aroundMessage',
    'page',
    'messages',
  ]),
  from: z.union([z.string().datetime({ offset: true }), z.null()]).optional(),
  to: z.union([z.string().datetime({ offset: true }), z.null()]).optional(),
  messageId: z.union([z.string().uuid(), z.null()]).optional(),
  cursor: z.union([z.string(), z.null()]).optional(),
  direction: z.union([z.enum(['older', 'newer']), z.null()]).optional(),
  messageIds: z.array(z.string().uuid()).optional(),
  limit: z.union([z.number().int().gte(0).lte(65535), z.null()]).optional(),
  includeThreadPreviews: z.union([z.boolean(), z.null()]).optional(),
  maxCharsPerMessage: z.union([z.number().int().gte(0), z.null()]).optional(),
});

export const ReadChannelMessagesResponse = z.object({
  channelId: z.string().uuid(),
  window: z.object({
    windowType: z.enum([
      'latest',
      'timeRange',
      'aroundMessage',
      'page',
      'messages',
    ]),
    from: z.union([z.string().datetime({ offset: true }), z.null()]).optional(),
    to: z.union([z.string().datetime({ offset: true }), z.null()]).optional(),
    messageId: z.union([z.string().uuid(), z.null()]).optional(),
    direction: z.union([z.enum(['older', 'newer']), z.null()]).optional(),
    messageIds: z.array(z.string().uuid()),
  }),
  messages: z.array(
    z.object({
      id: z.string().uuid(),
      channelId: z.string().uuid(),
      senderId: z.string(),
      content: z.string(),
      contentTruncated: z.boolean(),
      createdAt: z.string().datetime({ offset: true }),
      updatedAt: z.string().datetime({ offset: true }),
      editedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
      deletedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
      thread: z.object({
        threadId: z.string().uuid(),
        replyCount: z.number().int(),
        latestReplyAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
        preview: z
          .union([
            z.array(
              z.object({
                id: z.string().uuid(),
                threadId: z.string().uuid(),
                senderId: z.string(),
                content: z.string(),
                contentTruncated: z.boolean(),
                createdAt: z.string().datetime({ offset: true }),
                updatedAt: z.string().datetime({ offset: true }),
                editedAt: z
                  .union([z.string().datetime({ offset: true }), z.null()])
                  .optional(),
                reactions: z.array(
                  z.object({ emoji: z.string(), users: z.array(z.string()) })
                ),
                attachments: z.array(
                  z.object({
                    id: z.string().uuid(),
                    entityType: z.string(),
                    entityId: z.string(),
                    createdAt: z.string().datetime({ offset: true }),
                  })
                ),
              })
            ),
            z.null(),
          ])
          .optional(),
        omittedReplyCount: z.number().int(),
      }),
      reactions: z.array(
        z.object({ emoji: z.string(), users: z.array(z.string()) })
      ),
      attachments: z.array(
        z.object({
          id: z.string().uuid(),
          entityType: z.string(),
          entityId: z.string(),
          createdAt: z.string().datetime({ offset: true }),
        })
      ),
    })
  ),
  navigation: z.object({
    olderCursor: z.union([z.string(), z.null()]).optional(),
    newerCursor: z.union([z.string(), z.null()]).optional(),
    hasMoreOlder: z.boolean(),
    hasMoreNewer: z.boolean(),
  }),
  omissions: z.array(
    z.object({
      kind: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('olderMessages'),
          z.literal('newerMessages'),
          z.literal('threadReplies'),
          z.literal('truncatedContent'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      messageId: z.union([z.string().uuid(), z.null()]).optional(),
      threadId: z.union([z.string().uuid(), z.null()]).optional(),
      count: z.union([z.number().int(), z.null()]).optional(),
      cursor: z.union([z.string(), z.null()]).optional(),
    })
  ),
});

export const ReadChannelThread = z.object({
  channelId: z.string().uuid(),
  messageId: z.string().uuid(),
  windowType: z
    .union([z.enum(['allIfSmall', 'latest', 'aroundReply']), z.null()])
    .optional(),
  replyId: z.union([z.string().uuid(), z.null()]).optional(),
  before: z.union([z.number().int().gte(0).lte(65535), z.null()]).optional(),
  after: z.union([z.number().int().gte(0).lte(65535), z.null()]).optional(),
  limit: z.union([z.number().int().gte(0).lte(65535), z.null()]).optional(),
  includeChannelContext: z.union([z.boolean(), z.null()]).optional(),
  maxCharsPerMessage: z.union([z.number().int().gte(0), z.null()]).optional(),
});

export const ReadChannelThreadResponse = z.object({
  anchor: z.object({
    messageId: z.string().uuid(),
    channelId: z.string().uuid(),
    kind: z.any().superRefine((x, ctx) => {
      const schemas = [z.literal('topLevelMessage'), z.literal('threadReply')];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
    threadId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
  }),
  thread: z.object({
    channelId: z.string().uuid(),
    threadId: z.string().uuid(),
    parent: z.object({
      id: z.string().uuid(),
      channelId: z.string().uuid(),
      senderId: z.string(),
      content: z.string(),
      contentTruncated: z.boolean(),
      createdAt: z.string().datetime({ offset: true }),
      updatedAt: z.string().datetime({ offset: true }),
      editedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
      deletedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
      thread: z.object({
        threadId: z.string().uuid(),
        replyCount: z.number().int(),
        latestReplyAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
        preview: z
          .union([
            z.array(
              z.object({
                id: z.string().uuid(),
                threadId: z.string().uuid(),
                senderId: z.string(),
                content: z.string(),
                contentTruncated: z.boolean(),
                createdAt: z.string().datetime({ offset: true }),
                updatedAt: z.string().datetime({ offset: true }),
                editedAt: z
                  .union([z.string().datetime({ offset: true }), z.null()])
                  .optional(),
                reactions: z.array(
                  z.object({ emoji: z.string(), users: z.array(z.string()) })
                ),
                attachments: z.array(
                  z.object({
                    id: z.string().uuid(),
                    entityType: z.string(),
                    entityId: z.string(),
                    createdAt: z.string().datetime({ offset: true }),
                  })
                ),
              })
            ),
            z.null(),
          ])
          .optional(),
        omittedReplyCount: z.number().int(),
      }),
      reactions: z.array(
        z.object({ emoji: z.string(), users: z.array(z.string()) })
      ),
      attachments: z.array(
        z.object({
          id: z.string().uuid(),
          entityType: z.string(),
          entityId: z.string(),
          createdAt: z.string().datetime({ offset: true }),
        })
      ),
    }),
    replyCount: z.number().int().gte(0),
    latestReplyAt: z
      .union([z.string().datetime({ offset: true }), z.null()])
      .optional(),
  }),
  replies: z.array(
    z.object({
      id: z.string().uuid(),
      threadId: z.string().uuid(),
      senderId: z.string(),
      content: z.string(),
      contentTruncated: z.boolean(),
      createdAt: z.string().datetime({ offset: true }),
      updatedAt: z.string().datetime({ offset: true }),
      editedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
      reactions: z.array(
        z.object({ emoji: z.string(), users: z.array(z.string()) })
      ),
      attachments: z.array(
        z.object({
          id: z.string().uuid(),
          entityType: z.string(),
          entityId: z.string(),
          createdAt: z.string().datetime({ offset: true }),
        })
      ),
    })
  ),
  channelContext: z
    .union([
      z.array(
        z.object({
          id: z.string().uuid(),
          channelId: z.string().uuid(),
          senderId: z.string(),
          content: z.string(),
          contentTruncated: z.boolean(),
          createdAt: z.string().datetime({ offset: true }),
          updatedAt: z.string().datetime({ offset: true }),
          editedAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .optional(),
          deletedAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .optional(),
          thread: z.object({
            threadId: z.string().uuid(),
            replyCount: z.number().int(),
            latestReplyAt: z
              .union([z.string().datetime({ offset: true }), z.null()])
              .optional(),
            preview: z
              .union([
                z.array(
                  z.object({
                    id: z.string().uuid(),
                    threadId: z.string().uuid(),
                    senderId: z.string(),
                    content: z.string(),
                    contentTruncated: z.boolean(),
                    createdAt: z.string().datetime({ offset: true }),
                    updatedAt: z.string().datetime({ offset: true }),
                    editedAt: z
                      .union([z.string().datetime({ offset: true }), z.null()])
                      .optional(),
                    reactions: z.array(
                      z.object({
                        emoji: z.string(),
                        users: z.array(z.string()),
                      })
                    ),
                    attachments: z.array(
                      z.object({
                        id: z.string().uuid(),
                        entityType: z.string(),
                        entityId: z.string(),
                        createdAt: z.string().datetime({ offset: true }),
                      })
                    ),
                  })
                ),
                z.null(),
              ])
              .optional(),
            omittedReplyCount: z.number().int(),
          }),
          reactions: z.array(
            z.object({ emoji: z.string(), users: z.array(z.string()) })
          ),
          attachments: z.array(
            z.object({
              id: z.string().uuid(),
              entityType: z.string(),
              entityId: z.string(),
              createdAt: z.string().datetime({ offset: true }),
            })
          ),
        })
      ),
      z.null(),
    ])
    .optional(),
  omissions: z.array(
    z.object({
      kind: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('olderMessages'),
          z.literal('newerMessages'),
          z.literal('threadReplies'),
          z.literal('truncatedContent'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      messageId: z.union([z.string().uuid(), z.null()]).optional(),
      threadId: z.union([z.string().uuid(), z.null()]).optional(),
      count: z.union([z.number().int(), z.null()]).optional(),
      cursor: z.union([z.string(), z.null()]).optional(),
    })
  ),
});

export const ReadChat = z.object({ chatId: z.string() });

export const ReadChatResponse = z.object({
  chatId: z.string(),
  title: z.string(),
  messages: z.array(
    z.object({
      role: z.string(),
      content: z.string(),
      attachmentIds: z.array(z.string()),
    })
  ),
});

export const ReadContent = z.object({ documentId: z.string().uuid() });

export const ReadContentResponse = z.object({
  content: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.object({ text: z.string() }).strict(),
      z
        .object({
          markdown: z.array(
            z.any().superRefine((x, ctx) => {
              const schemas = [
                z.object({
                  nodeId: z.string(),
                  content: z.string(),
                  tag: z.string(),
                  type: z.literal('generic'),
                }),
                z.object({ url: z.string(), type: z.literal('staticImage') }),
                z.object({ id: z.string(), type: z.literal('dssImage') }),
              ];
              const errors = schemas.reduce<z.ZodError[]>(
                (errors, schema) =>
                  ((result) =>
                    result.error ? [...errors, result.error] : errors)(
                    schema.safeParse(x)
                  ),
                []
              );
              if (schemas.length - errors.length !== 1) {
                ctx.addIssue({
                  path: ctx.path,
                  code: 'invalid_union',
                  unionErrors: errors,
                  message: 'Invalid input: Should pass single schema',
                });
              }
            })
          ),
        })
        .strict(),
      z.object({ download: z.object({ url: z.string() }) }).strict(),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  comments: z.array(
    z.object({
      thread: z.object({
        threadId: z.number().int(),
        owner: z.string(),
        resolved: z.boolean(),
        documentId: z.string(),
        createdAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
        updatedAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
        deletedAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
        metadata: z.any().optional(),
      }),
      comments: z.array(
        z.object({
          commentId: z.number().int(),
          threadId: z.number().int(),
          order: z.union([z.number().int(), z.null()]).optional(),
          owner: z.string(),
          sender: z.union([z.string(), z.null()]).optional(),
          text: z.string(),
          metadata: z.any().optional(),
          createdAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .optional(),
          updatedAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .optional(),
          deletedAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .optional(),
        })
      ),
    })
  ),
});

export const ReadMetadata = z.object({ documentId: z.string().uuid() });

export const ReadMetadataResponse = z.object({
  documentMetadata: z.object({
    documentId: z.string(),
    documentVersionId: z.number().int(),
    owner: z.string(),
    documentName: z.string(),
    fileType: z.union([z.string(), z.null()]).optional(),
    sha: z.union([z.string(), z.null()]).optional(),
    projectId: z.union([z.string(), z.null()]).optional(),
    projectName: z.union([z.string(), z.null()]).optional(),
    branchedFromId: z.union([z.string(), z.null()]).optional(),
    branchedFromVersionId: z.union([z.number().int(), z.null()]).optional(),
    documentFamilyId: z.union([z.number().int(), z.null()]).optional(),
    documentBom: z.any().optional(),
    modificationData: z.any().optional(),
    createdAt: z
      .union([z.string().datetime({ offset: true }), z.null()])
      .optional(),
    updatedAt: z
      .union([z.string().datetime({ offset: true }), z.null()])
      .optional(),
    deletedAt: z
      .union([z.string().datetime({ offset: true }), z.null()])
      .optional(),
    subType: z
      .union([
        z.any().superRefine((x, ctx) => {
          const schemas = [
            z.literal('task'),
            z.literal('snippet'),
            z.literal('skill'),
            z.literal('initiative_description'),
          ];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        }),
        z.null(),
      ])
      .optional(),
    teamId: z.union([z.string(), z.null()]).optional(),
    teamTaskId: z.union([z.number().int(), z.null()]).optional(),
    content: z.object({
      state: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('unknown'),
          z.literal('pending'),
          z.literal('ready'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      location: z
        .union([
          z.any().superRefine((x, ctx) => {
            const schemas = [
              z.literal('object_storage'),
              z.literal('sync_service'),
              z.literal('docx_bom_parts'),
              z.literal('converted_pdf'),
              z.literal('unknown'),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
          z.null(),
        ])
        .optional(),
    }),
    branchName: z.union([z.string(), z.null()]).optional(),
  }),
  userAccessLevel: z.enum(['view', 'comment', 'edit', 'owner']),
});

export const ReadProject = z.object({ projectId: z.string().uuid() });

export const ReadProjectResponse = z.object({
  projectId: z.string().uuid(),
  projectName: z.string(),
  parentProjectId: z.union([z.string(), z.null()]).optional(),
  items: z.array(
    z.object({
      id: z.string(),
      itemType: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('document'),
          z.literal('chat'),
          z.literal('project'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      name: z.string(),
      fileType: z.union([z.string(), z.null()]).optional(),
      updatedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
    })
  ),
});

export const ReadSpreadsheet = z.object({
  documentId: z.string(),
  sheetId: z.union([z.string(), z.null()]).optional(),
  ranges: z.union([z.array(z.string()), z.null()]).optional(),
  includeStyles: z.union([z.boolean(), z.null()]).optional(),
});

export const RenameChannel = z.object({
  channelId: z.string().uuid(),
  name: z.string(),
});

export const RenameChannelResponse = z.object({
  channelId: z.string().uuid(),
  name: z.string(),
  previousName: z.union([z.string(), z.null()]).optional(),
  summary: z.string(),
});

export const RenameDocument = z.object({
  documentId: z.string().uuid(),
  documentName: z.string(),
});

export const RenameDocumentResponse = z.object({
  success: z.boolean(),
  documentId: z.string().uuid(),
  message: z.string(),
});

export const SearchSkills = z.object({
  name: z.string(),
  matchType: z
    .any()
    .superRefine((x, ctx) => {
      const schemas = [z.literal('partial'), z.literal('exact')];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    })
    .optional(),
});

export const SearchSkillsResponse = z.object({
  results: z.array(
    z.object({
      documentId: z.string().uuid(),
      name: z.string(),
      updatedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
    })
  ),
});

export const SearchTools = z.object({ query: z.string() });

export const SearchToolsResponse = z.object({
  results: z.array(z.object({ name: z.string(), description: z.string() })),
  additional_matches: z.array(
    z.object({ name: z.string(), description: z.string() })
  ),
});

export const SelfKnowledge = z.record(z.any());

export const SelfKnowledgeResponse = z.object({ about: z.string() });

export const SendChannelMessage = z.object({
  content: z.string(),
  channel_id: z.string().uuid(),
  thread_id: z.union([z.string().uuid(), z.null()]).optional(),
});

export const SendChannelMessageResponse = z.object({
  channel_id: z.string().uuid(),
  message_id: z.string(),
});

export const SendEmail = z.object({
  subject: z.string(),
  body: z.string(),
  to: z.array(
    z.object({
      email: z.string(),
      name: z.union([z.string(), z.null()]).optional(),
    })
  ),
  cc: z
    .array(
      z.object({
        email: z.string(),
        name: z.union([z.string(), z.null()]).optional(),
      })
    )
    .optional(),
  bcc: z
    .array(
      z.object({
        email: z.string(),
        name: z.union([z.string(), z.null()]).optional(),
      })
    )
    .optional(),
  replyingToId: z.union([z.string().uuid(), z.null()]).optional(),
  includeSignature: z.union([z.boolean(), z.null()]).optional(),
});

export const UserToolResponseForSendEmailResponse = z
  .any()
  .superRefine((x, ctx) => {
    const schemas = [
      z.literal('PendingUserExecution'),
      z.literal('Rejected'),
      z
        .object({
          UserAction: z.any().superRefine((x, ctx) => {
            const schemas = [
              z.literal('userEdited'),
              z
                .object({
                  sent: z.object({
                    message_id: z.string().uuid(),
                    thread_id: z.string().uuid(),
                  }),
                })
                .strict(),
              z
                .object({
                  convertedToDraft: z.object({ draft_id: z.string().uuid() }),
                })
                .strict(),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
        })
        .strict(),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  });

export const SetEntityProperty = z.object({
  entity_id: z.string(),
  entity_type: z.enum([
    'document',
    'project',
    'chat',
    'thread',
    'channel',
    'call',
    'user',
    'company',
  ]),
  property_definition_id: z.string().uuid(),
  boolean_value: z.union([z.boolean(), z.null()]).optional(),
  date_value: z
    .union([z.string().datetime({ offset: true }), z.null()])
    .optional(),
  number_value: z.union([z.number(), z.null()]).optional(),
  string_value: z.union([z.string(), z.null()]).optional(),
  option_id: z.union([z.string().uuid(), z.null()]).optional(),
  option_ids: z.union([z.array(z.string().uuid()), z.null()]).optional(),
  add_option_ids: z.union([z.array(z.string().uuid()), z.null()]).optional(),
  remove_option_ids: z.union([z.array(z.string().uuid()), z.null()]).optional(),
  entity_ref: z
    .union([
      z.object({
        entityType: z.enum([
          'document',
          'task',
          'project',
          'chat',
          'thread',
          'channel',
          'call',
          'user',
          'company',
        ]),
        entityId: z.string(),
      }),
      z.null(),
    ])
    .optional(),
  entity_refs: z
    .union([
      z.array(
        z.object({
          entityType: z.enum([
            'document',
            'task',
            'project',
            'chat',
            'thread',
            'channel',
            'call',
            'user',
            'company',
          ]),
          entityId: z.string(),
        })
      ),
      z.null(),
    ])
    .optional(),
  link_url: z.union([z.string(), z.null()]).optional(),
  link_urls: z.union([z.array(z.string()), z.null()]).optional(),
});

export const SetEntityPropertyResponse = z.object({
  success: z.boolean(),
  message: z.string(),
});

export const SetSenderPolicy = z.object({
  sender_email: z.string(),
  policy: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('signal'),
      z.literal('noise'),
      z.literal('block'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  thread_id: z.union([z.string().uuid(), z.null()]).optional(),
  inbox: z.union([z.string(), z.null()]).optional(),
});

export const SetSenderPolicyResponse = z.object({
  senderEmail: z.string(),
  policy: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('signal'),
      z.literal('noise'),
      z.literal('block'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  inbox: z.string(),
  summary: z.string(),
});

export const Subagent = z.object({ task: z.string() });

export const SubagentResponse = z.object({ result: z.string() });

export const TextEditorCodeExecution = z.object({ input: z.string() });

export const TextEditorCodeExecutionResponse = z.object({
  tool_use_id: z.string(),
  content: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.intersection(
        z.object({
          file_type: z.union([z.string(), z.null()]).optional(),
          content: z.union([z.string(), z.null()]).optional(),
          numLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          startLine: z.union([z.number().int().gte(0), z.null()]).optional(),
          totalLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          is_file_update: z.union([z.boolean(), z.null()]).optional(),
          oldStart: z.union([z.number().int().gte(0), z.null()]).optional(),
          oldLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          newStart: z.union([z.number().int().gte(0), z.null()]).optional(),
          newLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          lines: z.union([z.array(z.string()), z.null()]).optional(),
        }),
        z.object({ type: z.literal('text_editor_code_execution_view_result') })
      ),
      z.intersection(
        z.object({
          file_type: z.union([z.string(), z.null()]).optional(),
          content: z.union([z.string(), z.null()]).optional(),
          numLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          startLine: z.union([z.number().int().gte(0), z.null()]).optional(),
          totalLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          is_file_update: z.union([z.boolean(), z.null()]).optional(),
          oldStart: z.union([z.number().int().gte(0), z.null()]).optional(),
          oldLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          newStart: z.union([z.number().int().gte(0), z.null()]).optional(),
          newLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          lines: z.union([z.array(z.string()), z.null()]).optional(),
        }),
        z.object({
          type: z.literal('text_editor_code_execution_create_result'),
        })
      ),
      z.intersection(
        z.object({
          file_type: z.union([z.string(), z.null()]).optional(),
          content: z.union([z.string(), z.null()]).optional(),
          numLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          startLine: z.union([z.number().int().gte(0), z.null()]).optional(),
          totalLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          is_file_update: z.union([z.boolean(), z.null()]).optional(),
          oldStart: z.union([z.number().int().gte(0), z.null()]).optional(),
          oldLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          newStart: z.union([z.number().int().gte(0), z.null()]).optional(),
          newLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          lines: z.union([z.array(z.string()), z.null()]).optional(),
        }),
        z.object({
          type: z.literal('text_editor_code_execution_str_replace_result'),
        })
      ),
      z.intersection(
        z.object({
          error_code: z.any().superRefine((x, ctx) => {
            const schemas = [
              z.literal('unavailable'),
              z.literal('execution_time_exceeded'),
              z.literal('container_expired'),
              z.literal('invalid_input'),
              z.literal('too_many_requests'),
              z.literal('file_not_found'),
              z.literal('string_not_found'),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
        }),
        z.object({
          type: z.literal('text_editor_code_execution_tool_result_error'),
        })
      ),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
});

export const UpdateCalendarEvent = z.object({
  eventId: z.string().uuid(),
  calendarId: z.union([z.string().uuid(), z.null()]).optional(),
  scope: z.any().superRefine((x, ctx) => {
    const schemas = [z.literal('all'), z.literal('this_event')];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  recurrenceId: z.union([z.string(), z.null()]).optional(),
  title: z.union([z.string(), z.null()]).optional(),
  description: z.union([z.string(), z.null()]).optional(),
  location: z.union([z.string(), z.null()]).optional(),
  time: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.object({
            startsAt: z.string().datetime({ offset: true }),
            endsAt: z.string().datetime({ offset: true }),
            timeZone: z.union([z.string(), z.null()]).optional(),
            kind: z.literal('timed'),
          }),
          z.object({
            startDate: z.string().date(),
            endDate: z.string().date(),
            kind: z.literal('allDay'),
          }),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
  attendees: z
    .union([
      z.array(
        z.object({ email: z.string(), isOptional: z.boolean().optional() })
      ),
      z.null(),
    ])
    .optional(),
  recurrenceLines: z.union([z.array(z.string()), z.null()]).optional(),
  conference: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('google_meet'), z.literal('remove')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
  reminders: z
    .union([
      z.object({
        useDefault: z.boolean(),
        overrides: z
          .array(
            z.object({ method: z.string(), minutes: z.number().int().gte(0) })
          )
          .optional(),
      }),
      z.null(),
    ])
    .optional(),
  rsvp: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('accepted'),
          z.literal('declined'),
          z.literal('tentative'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
  outOfOffice: z
    .union([
      z.object({
        autoDeclineMode: z
          .union([
            z.any().superRefine((x, ctx) => {
              const schemas = [
                z.literal('decline_none'),
                z.literal('decline_all'),
                z.literal('decline_new_only'),
              ];
              const errors = schemas.reduce<z.ZodError[]>(
                (errors, schema) =>
                  ((result) =>
                    result.error ? [...errors, result.error] : errors)(
                    schema.safeParse(x)
                  ),
                []
              );
              if (schemas.length - errors.length !== 1) {
                ctx.addIssue({
                  path: ctx.path,
                  code: 'invalid_union',
                  unionErrors: errors,
                  message: 'Invalid input: Should pass single schema',
                });
              }
            }),
            z.null(),
          ])
          .optional(),
        declineMessage: z.union([z.string(), z.null()]).optional(),
      }),
      z.null(),
    ])
    .optional(),
});

export const ToolCalendarEvent = z.object({
  eventId: z.string().uuid(),
  title: z.string(),
  start: z.string(),
  end: z.string(),
  isAllDay: z.boolean(),
  timeZone: z.union([z.string(), z.null()]).optional(),
  location: z.union([z.string(), z.null()]).optional(),
  description: z.union([z.string(), z.null()]).optional(),
  status: z.string(),
  isRecurring: z.boolean(),
  recurrenceLines: z.array(z.string()),
  attendees: z.array(
    z.object({
      email: z.string(),
      responseStatus: z.string(),
      isOrganizer: z.boolean(),
      isOptional: z.boolean(),
    })
  ),
  attendeeCount: z.number().int().gte(0),
  organizerEmail: z.union([z.string(), z.null()]).optional(),
  conferenceUrl: z.union([z.string(), z.null()]).optional(),
  isReadOnly: z.boolean(),
  calendarId: z.union([z.string().uuid(), z.null()]).optional(),
});

export const UpdateReminder = z.object({
  reminderId: z.string().uuid(),
  description: z.union([z.string(), z.null()]).optional(),
  remindAt: z
    .union([z.string().datetime({ offset: true }), z.null()])
    .optional(),
  completed: z.union([z.boolean(), z.null()]).optional(),
});

export const UpdateThreadLabels = z.object({
  thread_id: z.string().uuid(),
  label_id: z.string().uuid(),
  add: z.boolean(),
});

export const UpdateThreadLabelsResponse = z.object({
  successfulCount: z.number().int().gte(0),
  failedCount: z.number().int().gte(0),
  summary: z.string(),
});

export const UploadFile = z.object({
  fileName: z.string(),
  contentBase64: z.string(),
  projectId: z.union([z.string().uuid(), z.null()]).optional(),
});

export const UploadFileResponse = z.object({
  documentId: z.string(),
  fileName: z.string(),
  sizeBytes: z.number().int().gte(0),
});

export const WebFetch = z.object({ input: z.string() });

export const WebFetchResponse = z.object({
  tool_use_id: z.string(),
  content: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.intersection(
        z.object({
          url: z.string(),
          content: z.object({
            source: z.any().superRefine((x, ctx) => {
              const schemas = [
                z.object({
                  media_type: z.string(),
                  data: z.string(),
                  type: z.literal('text'),
                }),
                z.object({
                  media_type: z.string(),
                  data: z.string(),
                  type: z.literal('base64'),
                }),
              ];
              const errors = schemas.reduce<z.ZodError[]>(
                (errors, schema) =>
                  ((result) =>
                    result.error ? [...errors, result.error] : errors)(
                    schema.safeParse(x)
                  ),
                []
              );
              if (schemas.length - errors.length !== 1) {
                ctx.addIssue({
                  path: ctx.path,
                  code: 'invalid_union',
                  unionErrors: errors,
                  message: 'Invalid input: Should pass single schema',
                });
              }
            }),
            title: z.union([z.string(), z.null()]).optional(),
            citations: z
              .union([z.object({ enabled: z.boolean() }), z.null()])
              .optional(),
          }),
          retrieved_at: z.string(),
        }),
        z.object({ type: z.literal('web_fetch_result') })
      ),
      z.intersection(
        z.object({
          error_code: z.any().superRefine((x, ctx) => {
            const schemas = [
              z.literal('invalid_input'),
              z.literal('url_too_long'),
              z.literal('url_not_allowed'),
              z.literal('url_not_accessible'),
              z.literal('too_many_requests'),
              z.literal('unsupported_content_type'),
              z.literal('max_uses_exceeded'),
              z.literal('unavailable'),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
        }),
        z.object({ type: z.literal('web_fetch_tool_result_error') })
      ),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
});

export const WebSearch = z.object({ input: z.string() });

export const WebSearchResponse = z.object({
  content: z.union([
    z.array(
      z.object({
        title: z.string(),
        url: z.string(),
        encrypted_content: z.union([z.string(), z.null()]).optional(),
        page_age: z.union([z.string(), z.null()]).optional(),
        type: z.literal('web_search_result'),
      })
    ),
    z.object({ type: z.string(), error_code: z.string() }),
  ]),
  tool_use_id: z.string(),
});

export const ReadThread = z.object({
  contentType: z.enum([
    'channel',
    'channel-message',
    'chat-thread',
    'chat-message',
    'project',
  ]),
  ids: z.array(z.string()),
  messagesSince: z
    .union([z.string().datetime({ offset: true }), z.null()])
    .optional(),
});

export const ReadResponse = z.object({
  content: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.object({
        channel_id: z.string(),
        channel_name: z.union([z.string(), z.null()]).optional(),
        transcript: z.string(),
        type: z.literal('channel'),
      }),
      z.object({
        conversation: z.array(
          z.object({
            chat_id: z.string(),
            title: z.string(),
            messages: z.array(
              z.object({
                content: z.string(),
                date: z.string().datetime({ offset: true }),
                attachmentIds: z.array(z.string()),
              })
            ),
          })
        ),
        type: z.literal('chat'),
      }),
      z.object({
        formatted_preview: z.string(),
        type: z.literal('itemPreviews'),
      }),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
});
